import { useState, useEffect, Fragment } from "react";
import { motion } from "framer-motion";
import { X, Check, Eraser, RotateCcw, ArrowUpDown, Sparkles, NotebookPen, HelpCircle, Volume2 } from "lucide-react";
import { playVoice } from "@/lib/tts";
import { parseArithmetic } from "@/lib/arithmetic";

const OPS = { add: "+", sub: "−", mul: "×", div: "÷" };

const SIGNS = {
  en: [
    { sym: "+", name: "Plus (add)", meaning: "Put things together to make more.", clues: ["altogether", "total", "in all", "both", "sum", "and ... more"], color: "bg-green-100" },
    { sym: "−", name: "Minus (take away)", meaning: "Take some away, or find the difference.", clues: ["how many left", "take away", "fewer", "difference", "how many more"], color: "bg-rose-100" },
    { sym: "×", name: "Times (multiply)", meaning: "Equal groups joined together.", clues: ["groups of", "each", "rows of", "times", "per"], color: "bg-sky-100" },
    { sym: "÷", name: "Divide (share)", meaning: "Share equally into groups.", clues: ["share equally", "split into", "each gets", "how many groups"], color: "bg-amber-100" },
  ],
  fr: [
    { sym: "+", name: "Plus (addition)", meaning: "Mettre ensemble pour avoir plus.", clues: ["en tout", "total", "au total", "les deux", "somme", "et ... de plus"], color: "bg-green-100" },
    { sym: "−", name: "Moins (soustraction)", meaning: "Enlever, ou trouver la différence.", clues: ["combien reste-t-il", "enlève", "moins", "différence", "combien de plus"], color: "bg-rose-100" },
    { sym: "×", name: "Fois (multiplication)", meaning: "Des groupes égaux mis ensemble.", clues: ["groupes de", "chaque", "rangées de", "fois", "par"], color: "bg-sky-100" },
    { sym: "÷", name: "Diviser (partager)", meaning: "Partager en parts égales.", clues: ["partager également", "répartir en", "chacun reçoit", "combien de groupes"], color: "bg-amber-100" },
  ],
};

function SignsHelper({ lang = "en" }) {
  const rows = SIGNS[lang] || SIGNS.en;
  const intro = lang === "fr"
    ? "Lis le problème et cherche ces mots-clés. Ils t'aident à choisir le bon signe."
    : "Read the problem and look for these clue words. They help you pick the right sign.";
  return (
    <div className="space-y-2.5" data-testid="signs-helper">
      <p className="text-sm font-parent font-semibold text-slate-600">{intro}</p>
      {rows.map((r) => (
        <div key={r.sym} className={`rounded-xl border-2 border-slate-900 p-2.5 ${r.color}`}>
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-white border-2 border-slate-900 flex items-center justify-center text-2xl font-black">{r.sym}</span>
            <span className="font-bold text-slate-900 flex-1">{r.name}</span>
            <button onClick={() => playVoice(`${r.name}. ${r.meaning} ${lang === "fr" ? "Mots-clés" : "Clue words"}: ${r.clues.join(", ")}`, lang)}
              data-testid={`signs-say-${r.sym === "+" ? "add" : r.sym === "−" ? "sub" : r.sym === "×" ? "mul" : "div"}`}
              className="w-8 h-8 rounded-full bg-teal-500 border-2 border-slate-900 flex items-center justify-center shrink-0" title="Hear this">
              <Volume2 size={14} color="#fff" />
            </button>
          </div>
          <p className="text-sm text-slate-700 mt-1">{r.meaning}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {r.clues.map((c) => (
              <span key={c} className="rounded-full bg-white border border-slate-400 px-2 py-0.5 text-xs font-semibold text-slate-700">{c}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
const onlyDigits = (s) => (s || "").replace(/[^0-9]/g, "").slice(0, 6);
// Allow digits and a single decimal point (for decimal calculations).
const numVal = (s) => {
  let v = (s || "").replace(/[^0-9.]/g, "");
  const i = v.indexOf(".");
  if (i >= 0) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "");
  return v.slice(0, 9);
};
// Split a decimal string into integer / fraction digit strings.
const decParts = (s) => {
  const [i = "", f = ""] = String(s ?? "").split(".");
  return { i: i.replace(/[^0-9]/g, ""), f: f.replace(/[^0-9]/g, "") };
};
// Clean decimal string (no float noise, no trailing zeros).
const fmtDec = (n) => {
  if (!isFinite(n)) return "";
  let s = Number(n).toFixed(8);
  if (s.includes(".")) s = s.replace(/0+$/, "").replace(/\.$/, "");
  return s;
};
const gcd = (x, y) => (y ? gcd(y, x % y) : Math.abs(x));

// Prime factors (ascending, with repeats), e.g. 12 -> [2, 2, 3].
const primeFactors = (n) => {
  const f = [];
  let x = Math.abs(n);
  for (let p = 2; p * p <= x; p++) { while (x % p === 0) { f.push(p); x /= p; } }
  if (x > 1) f.push(x);
  return f;
};
// Align the numbers' prime factors into shared columns so identical factors sit
// one under the other. A factor that repeats gets its own column each time.
// LCM = product of every column · HCF = product of columns filled in every row.
const buildAligned = (nums) => {
  const facs = nums.map(primeFactors);
  const maxCount = {};
  facs.forEach((f) => {
    const c = {};
    f.forEach((p) => { c[p] = (c[p] || 0) + 1; });
    Object.entries(c).forEach(([p, cnt]) => { maxCount[p] = Math.max(maxCount[p] || 0, cnt); });
  });
  const primes = Object.keys(maxCount).map(Number).sort((a, b) => a - b);
  const columns = [];
  primes.forEach((p) => { for (let k = 0; k < maxCount[p]; k++) columns.push(p); });
  const rows = facs.map((f) => {
    const remaining = {};
    f.forEach((p) => { remaining[p] = (remaining[p] || 0) + 1; });
    return columns.map((p) => { if (remaining[p] > 0) { remaining[p]--; return p; } return null; });
  });
  return { columns, rows };
};
const lcmOf = (a, b) => (a && b ? Math.abs(a * b) / gcd(a, b) : 0);
// Pull the first two "a/b" fractions out of a question (for the compare helper).
const parseFractions = (s) => {
  const out = [];
  const re = /\d+\/\d+/g;
  let m;
  while ((m = re.exec(s || "")) && out.length < 2) {
    const [n, d] = m[0].split("/");
    out.push({ n, d });
  }
  return out;
};
// True when the text contains a mixed number like "4 2/3" (whole then a fraction).
const hasMixed = (s) => /\d+\s+\d+\/\d+/.test(s || "");
// Standalone whole numbers in a question (not part of a fraction).
const parseWholes = (s) => {
  const out = [];
  const re = /\d+/g;
  let m;
  while ((m = re.exec(s || ""))) {
    const st = m.index, en = st + m[0].length;
    const b = st > 0 ? s[st - 1] : "";
    const a = en < s.length ? s[en] : "";
    if (!/[\d/]/.test(b) && !/[\d/]/.test(a)) out.push(m[0]);
  }
  return out;
};

// A single-digit worksheet box. Typing a digit auto-advances focus to the next
// box on the same row. Defined at module scope so the inputs are not remounted
// (and therefore keep focus) on every keystroke.
const advanceFocus = (box) => {
  const scope = box.closest("[data-adv-row]") || box.parentElement;
  if (!scope) return;
  const inputs = Array.from(scope.querySelectorAll("input[data-adv]"));
  const idx = inputs.indexOf(box);
  if (idx >= 0 && inputs[idx + 1]) inputs[idx + 1].focus();
};
const Cell = ({ value, onChange, small, testid }) => (
  <input value={value}
    onChange={(e) => { const raw = e.target.value; onChange(raw); if ((raw || "").replace(/[^0-9]/g, "")) advanceFocus(e.target); }}
    inputMode="numeric" maxLength={1} data-adv="1" data-testid={testid}
    className={`text-center font-bold rounded-md border-2 border-slate-900 outline-none focus:ring-2 focus:ring-blue-300 ${small ? "w-9 h-7 text-sm bg-amber-50 text-amber-700" : "w-9 h-10 text-xl bg-white"}`} />
);

// Small borrow annotations attached to a worksheet cell: a blue "1" on the left
// (the borrowed ten) and an amber box above (what's left after lending).
const BorrowBoxes = ({ k, annot, onChange }) => (
  <>
    <input value={annot[`${k}-t`] || ""} onChange={(e) => onChange(`${k}-t`, e.target.value)} inputMode="numeric" maxLength={1}
      data-testid={`calc-${k}-bten`}
      className="absolute top-0 -left-1.5 w-4 h-5 text-center text-[10px] font-bold rounded border border-blue-400 bg-blue-50 text-blue-600 outline-none z-20" placeholder="" />
    <input value={annot[`${k}-r`] || ""} onChange={(e) => onChange(`${k}-r`, e.target.value)} inputMode="numeric" maxLength={1}
      data-testid={`calc-${k}-bred`}
      className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-5 text-center text-[10px] font-bold rounded border border-amber-400 bg-amber-50 text-amber-700 outline-none z-10" placeholder="" />
  </>
);

export default function CalculationPad({ open, setOpen, question, label, storageKey, lang = "en" }) {
  const [op, setOp] = useState("add");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [addList, setAddList] = useState(["", ""]); // pre-layout addends (add mode)
  const [laid, setLaid] = useState(false);
  const [carry, setCarry] = useState([]); // top carry (simple mode)
  const [ans, setAns] = useState([]); // final answer / product row
  const [decAns, setDecAns] = useState(""); // answer for a decimal calculation
  const [decMul, setDecMul] = useState(false); // decimal multiplication (digit grid + place the point)
  const [decMulA, setDecMulA] = useState("");
  const [decMulB, setDecMulB] = useState("");
  const [sumCarry, setSumCarry] = useState([]); // carry for the addition of partials
  const [pRows, setPRows] = useState([]); // partial product rows (long mult)
  const [addends, setAddends] = useState([]); // multi-addend addition top numbers
  const [borrowRed, setBorrowRed] = useState([]); // subtraction: crossed-out reduced value
  const [borrowTen, setBorrowTen] = useState([]); // subtraction: the little borrowed 10 (1)
  const [crossed, setCrossed] = useState({}); // clicked/crossed-out source digits
  const [quotient, setQuotient] = useState("");
  const [remainder, setRemainder] = useState("");
  const [divQuot, setDivQuot] = useState([]); // quotient boxes on top (per dividend digit)
  const [divWork, setDivWork] = useState([]); // long-division working grid
  const [divCarry, setDivCarry] = useState([]); // short-division carried remainders (superscript before each digit)
  const [divSigns, setDivSigns] = useState([]); // long-division: per working-row operator sign (− / +)
  const [annot, setAnnot] = useState({}); // long-division working-cell borrow annotations (keyed)
  const setAnnotVal = (key, v) => setAnnot((p) => ({ ...p, [key]: (v || "").replace(/[^0-9]/g, "").slice(-1) }));
  // LCM / HCF prime-factor ladder
  const [factNums, setFactNums] = useState(["", ""]); // numbers to factor (entry)
  const [factTop, setFactTop] = useState([]); // the numbers shown on the top row after set-out
  const [factRows, setFactRows] = useState([]); // each: { p: "", q: [per number] }
  const [factLCM, setFactLCM] = useState("");
  const [factHCF, setFactHCF] = useState("");
  const [factMode, setFactMode] = useState("separate"); // "separate" (own column each) | "together" (shared prime)
  const [factExpr, setFactExpr] = useState([]); // per-number factor expression, e.g. "2 × 3 × 1"
  const [factGrid, setFactGrid] = useState(null); // auto-aligned prime factors { columns, rows }
  const [lcmExpr, setLcmExpr] = useState(""); // LCM as a product expression
  const [hcfExpr, setHcfExpr] = useState(""); // HCF as a product expression
  // Compare-fractions helper: convert two fractions to a common denominator (LCM)
  const [cmpN, setCmpN] = useState([{ n: "", d: "" }, { n: "", d: "" }]); // the two fractions
  const [cmpLcm, setCmpLcm] = useState("");   // common denominator (LCM of the denominators)
  const [cmpMul, setCmpMul] = useState(["", ""]); // multiplier for each fraction
  const [cmpNum, setCmpNum] = useState(["", ""]); // converted numerator for each fraction
  const [cmpOp, setCmpOp] = useState("");     // ">", "<" or "="
  // Fraction × fraction helper (inside the × section)
  const [mulFrac, setMulFrac] = useState(false);
  const [fmN, setFmN] = useState([{ n: "", d: "" }, { n: "", d: "" }]); // the two fractions
  const [fmR, setFmR] = useState({ n: "", d: "" }); // the product fraction
  const [fmRed, setFmRed] = useState({}); // cross-cancel reduced values, keys n0/d0/n1/d1
  const [fmDivOrig, setFmDivOrig] = useState({ n: "", d: "" }); // original 2nd fraction before flipping (÷)
  // Fraction +/− helper (find common denominator, convert, add/subtract numerators)
  const [arithFrac, setArithFrac] = useState(false);
  const [faN, setFaN] = useState([{ n: "", d: "" }, { n: "", d: "" }]);
  const [faLcm, setFaLcm] = useState("");
  const [faMul, setFaMul] = useState(["", ""]);
  const [faNum, setFaNum] = useState(["", ""]);
  const [faR, setFaR] = useState({ n: "", d: "" });
  const [fb, setFb] = useState(null);
  const [notes, setNotes] = useState(""); // free-form scratch working (ratios, proportions…)
  const [ratioLabels, setRatioLabels] = useState(["", ""]); // optional column names
  const [ratio, setRatio] = useState({ a: "", b: "", c: "", prod: "", x: "" }); // a:b = c:x

  const na = parseInt(a || "0", 10);
  const nb = parseInt(b || "0", 10);
  const useAddends = op === "add" && addends.length > 0;
  const addSum = addends.reduce((s, x) => s + parseInt(x || "0", 10), 0);
  const expected = op === "add" ? (useAddends ? addSum : na + nb) : op === "sub" ? na - nb : op === "mul" ? na * nb : null;
  const longMul = op === "mul" && b.length > 1;
  const termLens = useAddends ? addends.map((x) => x.length) : [a.length + (op === "mul" ? 1 : 0), b.length];
  const mulW = op === "mul" ? a.length + b.length : 0; // widest possible product
  const cols =
    op === "div"
      ? 0
      : Math.max(String(Math.abs(expected || 0)).length, mulW, ...termLens, 1);
  const pad = (s, n) => s.padStart(n, " ").split("");

  const setOperation = (k) => {
    setOp(k); setLaid(false); setFb(null); setAddends([]); setAddList(["", ""]); setFactNums(["", ""]); setMulFrac(false); setArithFrac(false); setDecMul(false);
    if (k === "cmp") {
      const fr = hasMixed(question) ? [] : parseFractions(question);
      setCmpN([{ n: fr[0]?.n || "", d: fr[0]?.d || "" }, { n: fr[1]?.n || "", d: fr[1]?.d || "" }]);
      setCmpLcm(""); setCmpMul(["", ""]); setCmpNum(["", ""]); setCmpOp("");
      if (fr.length >= 2) setLaid(true); // fractions found in the question -> go straight to converting
    }
    if (k === "notes") setLaid(true); // free-form notes: show the writing area straight away
    if (k === "ratio") { setRatio({ a: "", b: "", c: "", prod: "", x: "" }); setRatioLabels(["", ""]); setLaid(true); }
  };
  // Switch the × section into fraction mode: pre-load fractions/whole numbers
  // from the question (a whole number n becomes n/1) and open the working.
  const enableMulFrac = () => {
    setMulFrac(true);
    const mixed = hasMixed(question);
    const fr = mixed ? [] : parseFractions(question);
    const wholes = mixed ? [] : parseWholes(question);
    const f0 = fr[0] ? { n: fr[0].n, d: fr[0].d } : { n: "", d: "" };
    let f1 = { n: "", d: "" };
    if (fr[1]) f1 = { n: fr[1].n, d: fr[1].d };
    else if (wholes[0]) f1 = { n: wholes[0], d: "1" };
    setFmRed({});
    if (op === "div" && !mixed) {
      setFmDivOrig({ ...f1 });               // remember the divisor as written
      setFmN([f0, { n: f1.d, d: f1.n }]);     // "keep, change, flip" -> multiply by reciprocal
    } else {
      setFmDivOrig({ n: "", d: "" });
      setFmN([f0, f1]);
    }
    setFmR({ n: "", d: "" }); setFb(null); setLaid(true);
  };
  // Switch the +/− section into fraction mode: pre-load the two fractions.
  const enableArithFrac = () => {
    setArithFrac(true);
    const fr = hasMixed(question) ? [] : parseFractions(question);
    setFaN([{ n: fr[0]?.n || "", d: fr[0]?.d || "" }, { n: fr[1]?.n || "", d: fr[1]?.d || "" }]);
    setFaLcm(""); setFaMul(["", ""]); setFaNum(["", ""]); setFaR({ n: "", d: "" }); setFb(null); setLaid(true);
  };
  // A fraction with repeatable cross-cancel: a growing stack of red boxes above
  // the numerator and below the denominator. Each new value strikes the previous
  // one, so the child can divide again and again until nothing is left.
  const setRed = (key, idx, val) => setFmRed((p) => {
    const arr = Array.isArray(p[key]) ? [...p[key]] : [];
    arr[idx] = onlyDigits(val).slice(0, 7);
    while (arr.length && arr[arr.length - 1] === "") arr.pop();
    return { ...p, [key]: arr };
  });
  const redStack = (key, pos) => {
    const reds = Array.isArray(fmRed[key]) ? fmRed[key] : [];
    const n = Math.min(reds.length, 12);
    const items = [];
    for (let k = 0; k <= n; k++) {
      const struck = k < n - 1; // only the newest committed value stays un-struck
      items.push(
        <input key={`${key}-${k}`} value={reds[k] || ""} onChange={(e) => setRed(key, k, e.target.value)} inputMode="numeric" data-testid={`calc-fm-red-${key}-${k}`}
          className={`w-14 h-5 ${pos === "above" ? "mb-0.5" : "mt-0.5"} text-center text-xs font-bold rounded border outline-none ${struck ? "line-through text-slate-300 border-slate-200" : "text-red-600 border-red-300 bg-red-50"}`} />
      );
    }
    return pos === "above" ? items.slice().reverse() : items;
  };
  const hasRed = (key) => Array.isArray(fmRed[key]) && fmRed[key].length > 0;
  const fracCancel = (idx) => (
    <span className="inline-flex flex-col items-center leading-none">
      {redStack(`n${idx}`, "above")}
      <input value={fmN[idx].n} onChange={(e) => setFmN((p) => p.map((f, j) => (j === idx ? { ...f, n: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="?" inputMode="numeric" data-testid={`calc-fm-n-${idx}`}
        className={`w-16 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none ${hasRed(`n${idx}`) ? "line-through text-slate-400" : ""}`} />
      <span className="border-t-2 border-slate-900 w-full my-0.5" />
      <input value={fmN[idx].d} onChange={(e) => setFmN((p) => p.map((f, j) => (j === idx ? { ...f, d: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="?" inputMode="numeric" data-testid={`calc-fm-d-${idx}`}
        className={`w-16 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none ${hasRed(`d${idx}`) ? "line-through text-slate-400" : ""}`} />
      {redStack(`d${idx}`, "below")}
    </span>
  );

  // Lay out using explicit values (avoids stale state when prefilling).
  const applyLayout = (opv, av, bv, ads) => {
    const list = ads && ads.length ? ads : null;
    const nav = parseInt(av || "0", 10), nbv = parseInt(bv || "0", 10);
    const sum = list ? list.reduce((s, x) => s + parseInt(x || "0", 10), 0) : null;
    const exp = opv === "add" ? (list ? sum : nav + nbv) : opv === "sub" ? nav - nbv : opv === "mul" ? nav * nbv : null;
    if (opv === "sub" && nav < nbv) { setFb({ ok: false, msg: "Top number must be bigger." }); return false; }
    if (opv === "div" && nbv === 0) { setFb({ ok: false, msg: "Cannot divide by zero." }); return false; }
    const lens = list ? list.map((x) => x.length) : [av.length + (opv === "mul" ? 1 : 0), bv.length];
    const mulWv = opv === "mul" ? av.length + bv.length : 0; // widest possible product
    const c = opv === "div" ? 0 : Math.max(String(Math.abs(exp || 0)).length, mulWv, ...lens, 1);
    setFb(null);
    setAddends(list || []);
    setCarry(Array(c).fill(""));
    setSumCarry(Array(c).fill(""));
    setBorrowRed(Array(c).fill(""));
    setBorrowTen(Array(c).fill(""));
    setCrossed({});
    setAns(Array(opv === "div" ? 0 : c).fill(""));
    if (opv === "mul" && bv.length > 1) {
      const pw = c; // partial products can be as wide as the final product
      setPRows(String(nbv).split("").reverse().map(() => Array(pw).fill("")));
    } else {
      setPRows([]);
    }
    setQuotient(""); setRemainder("");
    if (opv === "div") {
      const dlen = String(nav).length;
      setDivQuot(Array(dlen).fill(""));
      setDivCarry(Array(dlen).fill(""));
      const rows = dlen + 1;
      setDivWork(Array(rows).fill(0).map(() => Array(dlen).fill("")));
      // pre-place the subtraction signs on alternate rows (0,2,4...)
      setDivSigns(Array(rows).fill("").map((_, r) => (r % 2 === 0 ? "−" : "")));
    } else {
      setDivQuot([]); setDivWork([]); setDivCarry([]); setDivSigns([]);
    }
    setAnnot({});
    setLaid(true);
    return true;
  };

  // When the question changes (or pad opens for a question), first try to
  // restore any saved work; otherwise pre-place the figures if it is a
  // straightforward operation, so the child needn't copy.
  useEffect(() => {
    if (!open) return;
    if (storageKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
        const cells = saved ? [
          ...(saved.ans || []), ...(saved.carry || []), ...(saved.sumCarry || []),
          ...(saved.borrowRed || []), ...(saved.borrowTen || []),
          ...(saved.divQuot || []), ...(saved.divCarry || []),
          ...((saved.pRows || []).flat()), ...((saved.divWork || []).flat()),
          saved.remainder || "",
        ] : [];
        const hasWork = saved && (
          cells.some((v) => v && String(v).trim()) ||
          (saved.notes && String(saved.notes).trim()) ||
          (saved.op === "ratio" && saved.ratio && Object.values(saved.ratio).some((v) => v && String(v).trim())) ||
          Object.values(saved.crossed || {}).some(Boolean) ||
          Object.values(saved.annot || {}).some((v) => v && String(v).trim()) ||
          (saved.decAns && String(saved.decAns).trim()) ||
          saved.decMul ||
          (saved.op === "fact" && !!(saved.factTop || []).length) ||
          (saved.op === "cmp" && ((saved.cmpMul || []).some((v) => v && String(v).trim()) || (saved.cmpNum || []).some((v) => v && String(v).trim()) || !!saved.cmpLcm || !!saved.cmpOp)) ||
          ((saved.op === "mul" || saved.op === "div") && saved.mulFrac && ((saved.fmR && (saved.fmR.n || saved.fmR.d)) || (saved.fmN || []).some((f) => f && (f.n || f.d)) || Object.keys(saved.fmRed || {}).length)) ||
          ((saved.op === "add" || saved.op === "sub") && saved.arithFrac && (!!saved.faLcm || (saved.faMul || []).some((v) => v) || (saved.faNum || []).some((v) => v) || (saved.faR && (saved.faR.n || saved.faR.d))))
        );
        // Only resume a snapshot that belongs to THIS exact question — a snapshot
        // saved for a different question at the same index must never leak in.
        const sameQuestion = saved && (saved.q ?? null) === (question ?? null);
        if (saved && saved.laid && hasWork && sameQuestion) {
          setOp(saved.op ?? "add"); setA(saved.a ?? ""); setB(saved.b ?? ""); setAddList(saved.addList ?? ["", ""]);
          setCarry(saved.carry ?? []); setAns(saved.ans ?? []); setDecAns(saved.decAns ?? ""); setDecMul(saved.decMul ?? false); setDecMulA(saved.decMulA ?? ""); setDecMulB(saved.decMulB ?? ""); setSumCarry(saved.sumCarry ?? []);
          setPRows(saved.pRows ?? []); setAddends(saved.addends ?? []);
          setBorrowRed(saved.borrowRed ?? []); setBorrowTen(saved.borrowTen ?? []); setCrossed(saved.crossed ?? {});
          setQuotient(saved.quotient ?? ""); setRemainder(saved.remainder ?? "");
          setDivQuot(saved.divQuot ?? []); setDivWork(saved.divWork ?? []); setDivCarry(saved.divCarry ?? []);
          setDivSigns(saved.divSigns ?? []); setAnnot(saved.annot ?? {});
          setFactTop(saved.factTop ?? []); setFactRows(saved.factRows ?? []); setFactLCM(saved.factLCM ?? ""); setFactHCF(saved.factHCF ?? ""); setFactMode(saved.factMode ?? "separate");
          setFactExpr(saved.factExpr ?? []); setLcmExpr(saved.lcmExpr ?? ""); setHcfExpr(saved.hcfExpr ?? ""); setFactGrid(saved.factGrid ?? null);
          setCmpN(saved.cmpN ?? [{ n: "", d: "" }, { n: "", d: "" }]); setCmpLcm(saved.cmpLcm ?? ""); setCmpMul(saved.cmpMul ?? ["", ""]); setCmpNum(saved.cmpNum ?? ["", ""]); setCmpOp(saved.cmpOp ?? "");
          setMulFrac(saved.mulFrac ?? false); setFmN(saved.fmN ?? [{ n: "", d: "" }, { n: "", d: "" }]); setFmR(saved.fmR ?? { n: "", d: "" }); setFmRed(saved.fmRed ?? {}); setFmDivOrig(saved.fmDivOrig ?? { n: "", d: "" });
          setArithFrac(saved.arithFrac ?? false); setFaN(saved.faN ?? [{ n: "", d: "" }, { n: "", d: "" }]); setFaLcm(saved.faLcm ?? ""); setFaMul(saved.faMul ?? ["", ""]); setFaNum(saved.faNum ?? ["", ""]); setFaR(saved.faR ?? { n: "", d: "" });
          setNotes(saved.notes ?? "");
          setRatioLabels(saved.ratioLabels ?? ["", ""]); setRatio(saved.ratio ?? { a: "", b: "", c: "", prod: "", x: "" });
          setFb(null); setLaid(true);
          return;
        }
      } catch (e) { /* ignore corrupt snapshot */ }
    }
    const parsed = parseArithmetic(question);
    if (parsed) {
      if (parsed.op === "add" && parsed.addends) {
        const ads = parsed.addends.map(onlyDigits).filter(Boolean);
        setOp("add"); setA(ads[0] || ""); setB(ads[1] || ""); setAddList(ads.length >= 2 ? ads : ["", ""]);
        applyLayout("add", ads[0] || "", ads[1] || "", ads);
      } else {
        const av = onlyDigits(parsed.a), bv = onlyDigits(parsed.b);
        setOp(parsed.op); setA(av); setB(bv);
        applyLayout(parsed.op, av, bv);
      }
    } else {
      setLaid(false); setA(""); setB(""); setAddends([]); setAddList(["", ""]); setFb(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, open, storageKey]);

  // Save the child's work so a paused session can be resumed.
  useEffect(() => {
    if (!open || !storageKey || !laid) return;
    const snap = { q: question ?? null, op, a, b, addList, laid, carry, ans, decAns, decMul, decMulA, decMulB, sumCarry, pRows, addends, borrowRed, borrowTen, crossed, quotient, remainder, divQuot, divWork, divCarry, divSigns, annot, factTop, factRows, factLCM, factHCF, factMode, factExpr, lcmExpr, hcfExpr, factGrid, cmpN, cmpLcm, cmpMul, cmpNum, cmpOp, mulFrac, fmN, fmR, fmRed, fmDivOrig, arithFrac, faN, faLcm, faMul, faNum, faR, notes, ratioLabels, ratio };
    try { localStorage.setItem(storageKey, JSON.stringify(snap)); } catch (e) { /* quota */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storageKey, laid, op, a, b, addList, carry, ans, sumCarry, pRows, addends, borrowRed, borrowTen, crossed, quotient, remainder, divQuot, divWork, divCarry, divSigns, annot, factTop, factRows, factLCM, factHCF, lcmExpr, hcfExpr, factGrid, cmpN, cmpLcm, cmpMul, cmpNum, cmpOp, mulFrac, fmN, fmR, fmRed, fmDivOrig, arithFrac, faN, faLcm, faMul, faNum, faR, decAns, decMul, decMulA, decMulB, notes, ratioLabels, ratio]);

  const decOperands = () => (op === "add" ? addList.filter((s) => s !== "") : [a, b]);
  const isDecimal = ["add", "sub", "mul", "div"].includes(op) && !mulFrac && !arithFrac && decOperands().some((s) => String(s).includes("."));
  // Column dimensions + result for a decimal calculation (used to line up the
  // decimal points and size the answer boxes). intW/fracW cover the widest of
  // the operands and the result so the point always stays aligned.
  const decDims = () => {
    const ops = decOperands();
    const nums = ops.map((s) => parseFloat(s)).filter((n) => !isNaN(n));
    let res;
    if (op === "add") res = nums.reduce((x, y) => x + y, 0);
    else if (op === "sub") res = nums[0] - nums[1];
    else if (op === "mul") res = nums[0] * nums[1];
    else res = nums[1] ? nums[0] / nums[1] : NaN;
    const parts = ops.map(decParts);
    const opIntW = Math.max(1, ...parts.map((p) => p.i.length));
    const opFracW = Math.max(0, ...parts.map((p) => p.f.length));
    const rp = decParts(fmtDec(res));
    const intW = Math.max(opIntW, rp.i.length);
    const fracW = Math.max(opFracW, rp.f.length);
    return { ops, nums, res, intW, fracW };
  };
  const layout = () => {
    if (op === "mul" && !mulFrac && (String(a).includes(".") || String(b).includes("."))) {
      const sa = a.replace(".", ""), sb = b.replace(".", "");
      if (!sa || !sb) { setFb({ ok: false, msg: "Enter both numbers." }); return; }
      setDecMul(true); setDecMulA(a); setDecMulB(b); setDecAns("");
      setA(sa); setB(sb);
      applyLayout("mul", sa, sb);
      setLaid(true);
      return;
    }
    if (isDecimal) {
      const ops = decOperands();
      if (ops.length < 2 || ops.some((s) => !s || s === ".")) { setFb({ ok: false, msg: "Enter the numbers." }); return; }
      if (op === "add" || op === "sub") {
        const { intW, fracW, res } = decDims();
        if (op === "sub" && res < 0) { setFb({ ok: false, msg: "Top number must be bigger." }); return; }
        const N = intW + fracW;
        setAns(Array(N).fill("")); setCarry(Array(N).fill(""));
        setBorrowTen(Array(N).fill("")); setBorrowRed(Array(N).fill("")); setCrossed({});
      }
      setDecAns(""); setFb(null); setLaid(true);
      return;
    }
    if ((op === "add" || op === "sub") && arithFrac) {
      const ok = faN.every((f) => f.n && f.d && parseInt(f.d, 10) > 0);
      if (!ok) { setFb({ ok: false, msg: "Enter both fractions (top and bottom)." }); return; }
      setFb(null); setLaid(true);
      return;
    }
    if ((op === "mul" || op === "div") && mulFrac) {
      const ok = fmN.every((f) => f.n && f.d && parseInt(f.d, 10) > 0);
      if (!ok) { setFb({ ok: false, msg: "Enter both fractions (top and bottom)." }); return; }
      setFb(null); setLaid(true);
      return;
    }
    if (op === "cmp") {
      const ok = cmpN.every((f) => f.n && f.d && parseInt(f.d, 10) > 0 && parseInt(f.n, 10) >= 0);
      if (!ok) { setFb({ ok: false, msg: "Enter both fractions (top and bottom)." }); return; }
      setFb(null); setLaid(true);
      return;
    }
    if (op === "fact") {
      const nums = factNums.map(onlyDigits).filter((x) => x && parseInt(x, 10) > 1);
      if (nums.length < 1) { setFb({ ok: false, msg: "Enter at least one number." }); return; }
      setFactTop(nums);
      setFactRows([{ p: nums.map(() => ""), q: nums.map(() => "") }]);
      setFactExpr(nums.map(() => "")); setLcmExpr(""); setHcfExpr(""); setFactGrid(null);
      setFactLCM(""); setFactHCF(""); setFb(null); setLaid(true);
      return;
    }
    if (op === "add") {
      const ads = addList.map(onlyDigits).filter(Boolean);
      if (ads.length < 2) { setFb({ ok: false, msg: "Enter at least two numbers." }); return; }
      applyLayout("add", ads[0], ads[1] || "", ads);
      return;
    }
    if (!a || !b) return;
    applyLayout(op, a, b);
  };
  const addFactRow = () => setFactRows((prev) => [...prev, { p: factTop.map(() => ""), q: factTop.map(() => "") }]);
  const isPrime = (p) => { if (p < 2) return false; for (let d = 2; d * d <= p; d++) if (p % d === 0) return false; return true; };
  // Check the child has actually divided every number all the way down to 1
  // (using prime divisors, arithmetically consistent). Returns the offending
  // number's index, or null when the ladder is complete and correct.
  const ladderIncomplete = () => {
    const nums = factTop.map((x) => parseInt(x, 10));
    for (let i = 0; i < nums.length; i++) {
      let cur = nums[i];
      for (let r = 0; r < factRows.length && cur !== 1; r++) {
        const row = factRows[r];
        const p = parseInt((factMode === "together" ? row.p[0] : row.p[i]) || "0", 10);
        const qRaw = (row.q[i] || "").trim();
        if (!qRaw || !p) return i;           // a division is still missing
        if (!isPrime(p)) return i;           // must divide by a prime
        const q = parseInt(qRaw, 10);
        const expected = cur % p === 0 ? cur / p : (factMode === "together" ? cur : NaN);
        if (q !== expected) return i;        // wrong quotient
        cur = q;
      }
      if (cur !== 1) return i;               // never reached 1
    }
    return null;
  };
  // Auto-fill: factor every number, align identical primes into columns, and
  // work out LCM / HCF for the child.
  const autoFillFactors = () => {
    const nums = factTop.map((x) => parseInt(x, 10)).filter((x) => x > 1);
    if (!nums.length) return;
    const bad = ladderIncomplete();
    if (bad !== null) {
      setFb({ ok: false, msg: `Termine d'abord les divisions : divise chaque nombre jusqu'à 1 (regarde ${factTop[bad]}).` });
      return;
    }
    // only lines up the factors; the child still works out the LCM and HCF
    setFactGrid(buildAligned(nums));
    setFb({ ok: true, msg: "Factors lined up! Now write the LCM and the HCF yourself. / Calcule maintenant le PPCM (LCM) et le PGCD (HCF)." });
  };
  const setFactPrime = (r, i, v) => setFactRows((prev) => prev.map((row, j) => (j === r ? { ...row, p: row.p.map((x, k) => (k === i ? onlyDigits(v).slice(0, 3) : x)) } : row)));
  const setFactQuot = (r, i, v) => setFactRows((prev) => prev.map((row, j) => (j === r ? { ...row, q: row.q.map((x, k) => (k === i ? onlyDigits(v).slice(0, 6) : x)) } : row)));

  const blank = (...v) => v.flat().every((x) => !String(x ?? "").trim());
  const notDone = () => {
    if (op === "ratio") return blank(ratio.prod, ratio.x);
    if (decMul) return blank(ans, decAns);
    if (isDecimal) return op === "add" || op === "sub" ? blank(ans) : blank(decAns);
    if ((op === "add" || op === "sub") && arithFrac) return blank(faLcm, faR.n, faR.d);
    if ((op === "mul" || op === "div") && mulFrac) return blank(fmR.n, fmR.d);
    if (op === "cmp") return blank(cmpLcm, cmpOp);
    if (op === "fact") return blank(factLCM, factHCF);
    if (op === "div") return blank(divQuot, remainder);
    return blank(ans);
  };

  // the copybook never gives the answer: the child must do the working and find it
  const check = () => {
    if (notDone()) { setFb({ ok: false, msg: "Do the exercise first: write your working and your answer in the boxes, then press Check. / Fais d'abord l'exercice, puis appuie sur Check." }); return; }
    if (op === "ratio") {
      const a = parseFloat(ratio.a), b = parseFloat(ratio.b), c = parseFloat(ratio.c);
      const prod = parseFloat(ratio.prod), x = parseFloat(ratio.x);
      if (isNaN(a) || isNaN(b) || isNaN(c) || a === 0) { setFb({ ok: false, msg: "Remplis a, b et c (a ne peut pas être 0)." }); return; }
      const prodOk = !isNaN(prod) && Math.abs(prod - b * c) < 1e-6;
      const trueX = (b * c) / a;
      const xOk = !isNaN(x) && Math.abs(x - trueX) <= Math.max(0.05, Math.abs(trueX) * 0.005);
      if (prodOk && xOk) setFb({ ok: true, msg: "Correct!" });
      else if (!prodOk) setFb({ ok: false, msg: "Multiplie d'abord b × c." });
      else setFb({ ok: false, msg: "Divise (b × c) par a." });
      return;
    }
    if (decMul) {
      const dp = parseInt((decMulA || "").replace(".", ""), 10) * parseInt((decMulB || "").replace(".", ""), 10);
      const gridNum = parseInt((ans.join("") || "").replace(/\D/g, "") || "-1", 10);
      const trueP = parseFloat(decMulA) * parseFloat(decMulB);
      const got = parseFloat(decAns);
      if (gridNum !== dp) { setFb({ ok: false, msg: "Continue la multiplication des chiffres." }); return; }
      const ok = !isNaN(got) && Math.abs(got - trueP) < 1e-6;
      setFb(ok ? { ok: true, msg: "Correct!" } : { ok: false, msg: "Place bien la virgule dans la réponse." });
      return;
    }
    if (isDecimal) {
      const { intW, fracW, res } = decDims();
      if (op === "add" || op === "sub") {
        let str = ans.slice(0, intW).join("").replace(/\s/g, "");
        if (fracW > 0) str += "." + ans.slice(intW, intW + fracW).join("").replace(/\s/g, "");
        const got = parseFloat(str);
        const ok = !isNaN(got) && !isNaN(res) && Math.abs(got - res) < 1e-9;
        setFb(ok ? { ok: true, msg: "Correct!" } : { ok: false, msg: "Pas tout à fait — garde le point aligné et recalcule." });
        return;
      }
      const got = parseFloat(decAns);
      const ok = !isNaN(got) && !isNaN(res) && Math.abs(got - res) < 1e-6;
      setFb(ok ? { ok: true, msg: "Correct!" } : { ok: false, msg: "Pas tout à fait — calcule et réessaie." });
      return;
    }
    if ((op === "add" || op === "sub") && arithFrac) {
      const a = parseInt(faN[0].n, 10), c = parseInt(faN[0].d, 10);
      const b = parseInt(faN[1].n, 10), d = parseInt(faN[1].d, 10);
      if (!(c > 0) || !(d > 0) || isNaN(a) || isNaN(b)) { setFb({ ok: false, msg: "Remplis les deux fractions." }); return; }
      const want = lcmOf(c, d);
      const m0 = parseInt(faMul[0] || "0", 10), m1 = parseInt(faMul[1] || "0", 10);
      const cn0 = parseInt(faNum[0] || "-999", 10), cn1 = parseInt(faNum[1] || "-999", 10);
      const rn = parseInt(faR.n, 10), rd = parseInt(faR.d, 10);
      const lcmOk = parseInt(faLcm || "0", 10) === want;
      const convOk = m0 * c === want && m1 * d === want && cn0 === a * m0 && cn1 === b * m1;
      const trueNum = op === "add" ? a * (want / c) + b * (want / d) : a * (want / c) - b * (want / d);
      const resOk = Number.isFinite(rn) && Number.isFinite(rd) && rd > 0 && rn * want === trueNum * rd;
      if (lcmOk && convOk && resOk) setFb({ ok: true, msg: "Correct!" });
      else if (!lcmOk) setFb({ ok: false, msg: "Vérifie le dénominateur commun (LCM)." });
      else if (!convOk) setFb({ ok: false, msg: "Vérifie tes conversions (multiplie haut et bas)." });
      else setFb({ ok: false, msg: `${op === "add" ? "Additionne" : "Soustrais"} les numérateurs, puis réessaie.` });
      return;
    }
    if ((op === "mul" || op === "div") && mulFrac) {
      const n1 = parseInt(fmN[0].n, 10), d1 = parseInt(fmN[0].d, 10);
      const n2 = parseInt(fmN[1].n, 10), d2 = parseInt(fmN[1].d, 10);
      const rn = parseInt(fmR.n, 10), rd = parseInt(fmR.d, 10);
      if (!(d1 > 0) || !(d2 > 0) || isNaN(n1) || isNaN(n2)) { setFb({ ok: false, msg: "Remplis les deux fractions." }); return; }
      const pn = n1 * n2, pd = d1 * d2;
      const ok = Number.isFinite(rn) && Number.isFinite(rd) && rd > 0 && rn * pd === pn * rd;
      setFb(ok ? { ok: true, msg: "Correct!" } : { ok: false, msg: "Multiplie les numérateurs entre eux et les dénominateurs entre eux, puis réessaie." });
      return;
    }
    if (op === "cmp") {
      const d0 = parseInt(cmpN[0].d, 10), d1 = parseInt(cmpN[1].d, 10);
      const n0 = parseInt(cmpN[0].n, 10), n1 = parseInt(cmpN[1].n, 10);
      const want = lcmOf(d0, d1);
      const m0 = parseInt(cmpMul[0] || "0", 10), m1 = parseInt(cmpMul[1] || "0", 10);
      const cn0 = parseInt(cmpNum[0] || "-1", 10), cn1 = parseInt(cmpNum[1] || "-1", 10);
      const lcmOk = parseInt(cmpLcm || "0", 10) === want;
      const convOk = m0 * d0 === want && m1 * d1 === want && cn0 === n0 * m0 && cn1 === n1 * m1;
      const trueOp = n0 * d1 > n1 * d0 ? ">" : n0 * d1 < n1 * d0 ? "<" : "=";
      const opOk = cmpOp === trueOp;
      if (lcmOk && convOk && opOk) { setFb({ ok: true, msg: "Correct!" }); }
      else if (!lcmOk) setFb({ ok: false, msg: "Vérifie le dénominateur commun (LCM)." });
      else if (!convOk) setFb({ ok: false, msg: "Vérifie tes conversions (multiplie haut et bas)." });
      else setFb({ ok: false, msg: "Compare les numérateurs et choisis le bon signe." });
      return;
    }
    if (op === "fact") {
      const nums = factTop.map((x) => parseInt(x, 10));
      let h = nums[0]; let l = nums[0];
      for (let k = 1; k < nums.length; k++) { h = gcd(h, nums[k]); l = (l * nums[k]) / gcd(l, nums[k]); }
      const gL = String(l), gH = String(h);
      const okL = factLCM.replace(/[^0-9]/g, "") === gL;
      const okH = factHCF.replace(/[^0-9]/g, "") === gH;
      if (okL && okH) { setFb({ ok: true, msg: "Correct!" }); }
      else setFb({ ok: false, msg: "Not quite, check your working and try again! / Pas tout à fait, vérifie ton calcul et réessaie !" });
      return;
    }
    if (op === "div") {
      const eq = Math.floor(na / nb), er = na % nb;
      const qStr = divQuot.join("").replace(/^0+/, "") || "0";
      const rem = remainder === "" ? 0 : parseInt(remainder, 10);
      const ok = parseInt(qStr, 10) === eq && rem === er;
      setFb(ok ? { ok: true, msg: "Correct!" } : { ok: false, msg: "Not quite, check your working and try again! / Pas tout à fait, vérifie ton calcul et réessaie !" });
      return;
    }
    const joined = parseInt(ans.join("").replace(/[^0-9]/g, "") || "-1", 10);
    setFb(joined === expected ? { ok: true, msg: "Correct!" } : { ok: false, msg: "Not quite, check your working and try again! / Pas tout à fait, vérifie ton calcul et réessaie !" });
  };

  const setCell = (arr, setArr, i, v) => { const n = [...arr]; n[i] = onlyDigits(v).slice(-1); setArr(n); };
  const setNested = (r, i, v) => {
    setPRows((prev) => {
      const copy = prev.map((row) => [...row]);
      copy[r][i] = onlyDigits(v).slice(-1);
      return copy;
    });
  };
  const setWork = (r, i, v) => {
    setDivWork((prev) => {
      const copy = prev.map((row) => [...row]);
      copy[r][i] = onlyDigits(v).slice(-1);
      return copy;
    });
  };
  const setDivSign = (r, v) => {
    const ch = (v || "").slice(-1);
    const sign = ch === "+" ? "+" : (ch === "-" || ch === "−") ? "−" : "";
    setDivSigns((prev) => prev.map((s, j) => (j === r ? sign : s)));
  };
  const addWorkRow = () => {
    const w = (divWork[0] && divWork[0].length) || String(a).length || 1;
    setDivWork((prev) => [...prev, Array(w).fill("")]);
    setDivSigns((prev) => [...prev, prev.length % 2 === 0 ? "−" : ""]);
  };
  const reset = () => { if (storageKey) { try { localStorage.removeItem(storageKey); } catch (e) {} } setLaid(false); setA(""); setB(""); setAddList(["", ""]); setFactNums(["", ""]); setFactTop([]); setFactRows([]); setFactLCM(""); setFactHCF(""); setFactMode("separate"); setFactExpr([]); setLcmExpr(""); setHcfExpr(""); setFactGrid(null); setCmpN([{ n: "", d: "" }, { n: "", d: "" }]); setCmpLcm(""); setCmpMul(["", ""]); setCmpNum(["", ""]); setCmpOp(""); setMulFrac(false); setFmN([{ n: "", d: "" }, { n: "", d: "" }]); setFmR({ n: "", d: "" }); setFmRed({}); setFmDivOrig({ n: "", d: "" }); setArithFrac(false); setFaN([{ n: "", d: "" }, { n: "", d: "" }]); setFaLcm(""); setFaMul(["", ""]); setFaNum(["", ""]); setFaR({ n: "", d: "" }); setDecAns(""); setDecMul(false); setDecMulA(""); setDecMulB(""); setNotes(""); setRatio({ a: "", b: "", c: "", prod: "", x: "" }); setRatioLabels(["", ""]); setFb(null); setPRows([]); setAddends([]); setBorrowRed([]); setBorrowTen([]); setCrossed({}); setDivQuot([]); setDivWork([]); setDivCarry([]); setDivSigns([]); setAnnot({}); setRemainder(""); };
  const clearWork = () => {
    setCarry(Array(cols).fill(""));
    setSumCarry(Array(cols).fill(""));
    setBorrowRed(Array(cols).fill(""));
    setBorrowTen(Array(cols).fill(""));
    setCrossed({});
    setAns(Array(cols).fill(""));
    setPRows((prev) => prev.map((row) => row.map(() => "")));
    setDivQuot((prev) => prev.map(() => ""));
    setDivWork((prev) => prev.map((row) => row.map(() => "")));
    setDivCarry((prev) => prev.map(() => ""));
    setDivSigns((prev) => prev.map(() => ""));
    setAnnot({});
    setQuotient(""); setRemainder(""); setFb(null);
  };
  const swap = () => { const t = a; setA(b); setB(t); applyLayout(op, b, t); };

  const Spacer = null;
  // A source digit the child can click to cross out (helps track digits used).
  const DigitCell = ({ id, ch }) => {
    const blank = !ch || !ch.trim();
    const on = !!crossed[id];
    return (
      <div
        onClick={() => { if (!blank) setCrossed((p) => ({ ...p, [id]: !p[id] })); }}
        data-testid={`calc-digit-${id}`}
        className={`w-9 h-10 flex items-center justify-center text-xl font-bold select-none ${blank ? "" : "cursor-pointer"} ${on ? "line-through text-slate-400" : ""}`}>
        {ch.trim()}
      </div>
    );
  };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      className="nb-card bg-white p-4 font-kids md:sticky md:top-4"
      data-testid="calc-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-parent font-semibold text-slate-500">{label || "Copybook — work it out"}</p>
        <button onClick={() => setOpen(false)} data-testid="calc-close" className="w-8 h-8 rounded-full border-2 border-slate-900 flex items-center justify-center"><X size={16} /></button>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {Object.entries(OPS).map(([k, sym]) => (
          <button key={k} onClick={() => setOperation(k)} data-testid={`calc-op-${k}`}
            className={`w-10 h-10 rounded-lg border-2 border-slate-900 font-bold text-lg ${k === op ? "bg-blue-500 text-white" : "bg-white"}`}>{sym}</button>
        ))}
        <button onClick={() => setOperation("fact")} data-testid="calc-op-fact"
          className={`h-10 px-3 rounded-lg border-2 border-slate-900 font-bold text-sm ${op === "fact" ? "bg-blue-500 text-white" : "bg-white"}`}>LCM / HCF</button>
        <button onClick={() => setOperation("cmp")} data-testid="calc-op-cmp"
          className={`h-10 px-3 rounded-lg border-2 border-slate-900 font-bold text-sm ${op === "cmp" ? "bg-blue-500 text-white" : "bg-white"}`}>Compare ⚖</button>
        <button onClick={() => setOperation("notes")} data-testid="calc-op-notes"
          className={`h-10 px-3 rounded-lg border-2 border-slate-900 font-bold text-sm inline-flex items-center gap-1.5 ${op === "notes" ? "bg-blue-500 text-white" : "bg-white"}`}><NotebookPen size={15} /> Notes</button>
        <button onClick={() => setOperation("ratio")} data-testid="calc-op-ratio"
          className={`h-10 px-3 rounded-lg border-2 border-slate-900 font-bold text-sm ${op === "ratio" ? "bg-blue-500 text-white" : "bg-white"}`}>Ratio :</button>
        <button onClick={() => setOperation("signs")} data-testid="calc-op-signs"
          className={`h-10 px-3 rounded-lg border-2 border-slate-900 font-bold text-sm inline-flex items-center gap-1.5 ${op === "signs" ? "bg-blue-500 text-white" : "bg-amber-200"}`}><HelpCircle size={15} /> {lang === "fr" ? "Quel signe ?" : "Which sign?"}</button>
      </div>

            {op === "signs" ? (
              <SignsHelper lang={lang} />
            ) : !laid ? (
              <div className="space-y-2">
                {op === "cmp" ? (
                  <div className="space-y-3" data-testid="calc-cmp-entry">
                    <p className="font-parent text-xs text-slate-500">Enter the two fractions to compare.</p>
                    <div className="flex items-center gap-4">
                      {[0, 1].map((i) => (
                        <span key={i} className="inline-flex flex-col items-center gap-1">
                          <input value={cmpN[i].n} onChange={(e) => setCmpN((p) => p.map((f, j) => (j === i ? { ...f, n: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="n" data-testid={`calc-cmp-n-${i}`}
                            className="w-14 h-10 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                          <span className="w-14 border-t-2 border-slate-900" />
                          <input value={cmpN[i].d} onChange={(e) => setCmpN((p) => p.map((f, j) => (j === i ? { ...f, d: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="d" data-testid={`calc-cmp-d-${i}`}
                            className="w-14 h-10 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                        </span>
                      ))}
                    </div>
                  </div>
                ) : op === "fact" ? (
                  <div className="space-y-2" data-testid="calc-factlist">
                    <p className="font-parent text-xs text-slate-500">Enter the numbers to break into prime factors (for LCM / HCF).</p>
                    {factNums.map((val, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input value={val} onChange={(e) => setFactNums((p) => p.map((x, j) => (j === i ? onlyDigits(e.target.value) : x)))} placeholder="0" data-testid={`calc-fact-${i}`}
                          className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                        {factNums.length > 1 && (
                          <button onClick={() => setFactNums((p) => p.filter((_, j) => j !== i))} data-testid={`calc-fact-remove-${i}`}
                            className="w-7 h-7 rounded-full border-2 border-slate-300 text-slate-500 flex items-center justify-center"><X size={13} /></button>
                        )}
                      </div>
                    ))}
                    {factNums.length < 4 && (
                      <button onClick={() => setFactNums((p) => [...p, ""])} data-testid="calc-fact-more"
                        className="rounded-full border-2 border-slate-300 text-slate-600 text-xs font-parent font-semibold px-3 py-1.5">+ Add another number</button>
                    )}
                  </div>
                ) : op === "add" ? (
                  <div className="space-y-3">
                    <div className="flex gap-2 font-parent">
                      <button onClick={() => setArithFrac(false)} data-testid="calc-add-whole"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${!arithFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Whole +</button>
                      <button onClick={enableArithFrac} data-testid="calc-add-frac"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${arithFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Fraction +</button>
                    </div>
                    <div className="space-y-2" data-testid="calc-addlist">
                    {addList.map((val, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-2xl font-bold w-5 text-center">{i === 0 ? "" : "+"}</span>
                        <input value={val} onChange={(e) => setAddList((p) => p.map((x, j) => (j === i ? numVal(e.target.value) : x)))} placeholder="0" data-testid={`calc-add-${i}`}
                          className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                        {addList.length > 2 && (
                          <button onClick={() => setAddList((p) => p.filter((_, j) => j !== i))} data-testid={`calc-add-remove-${i}`}
                            className="w-7 h-7 rounded-full border-2 border-slate-300 text-slate-500 flex items-center justify-center"><X size={13} /></button>
                        )}
                      </div>
                    ))}
                    {addList.length < 6 && (
                      <button onClick={() => setAddList((p) => [...p, ""])} data-testid="calc-add-more"
                        className="rounded-full border-2 border-slate-300 text-slate-600 text-xs font-parent font-semibold px-3 py-1.5 inline-flex items-center gap-1">+ Add another number</button>
                    )}
                    </div>
                  </div>
                ) : op === "mul" ? (
                  <div className="space-y-3">
                    <div className="flex gap-2 font-parent">
                      <button onClick={() => setMulFrac(false)} data-testid="calc-mul-whole"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${!mulFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Whole ×</button>
                      <button onClick={enableMulFrac} data-testid="calc-mul-frac"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${mulFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Fraction ×</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input value={a} onChange={(e) => setA(numVal(e.target.value))} placeholder="0" data-testid="calc-a"
                        className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      <span className="text-2xl font-bold">×</span>
                      <input value={b} onChange={(e) => setB(numVal(e.target.value))} placeholder="0" data-testid="calc-b"
                        className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                    </div>
                  </div>
                ) : op === "sub" ? (
                  <div className="space-y-3">
                    <div className="flex gap-2 font-parent">
                      <button onClick={() => setArithFrac(false)} data-testid="calc-sub-whole"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${!arithFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Whole −</button>
                      <button onClick={enableArithFrac} data-testid="calc-sub-frac"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${arithFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Fraction −</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input value={a} onChange={(e) => setA(numVal(e.target.value))} placeholder="0" data-testid="calc-a"
                        className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      <span className="text-2xl font-bold">−</span>
                      <input value={b} onChange={(e) => setB(numVal(e.target.value))} placeholder="0" data-testid="calc-b"
                        className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                    </div>
                  </div>
                ) : op === "div" ? (
                  <div className="space-y-3">
                    <div className="flex gap-2 font-parent">
                      <button onClick={() => setMulFrac(false)} data-testid="calc-div-whole"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${!mulFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Whole ÷</button>
                      <button onClick={enableMulFrac} data-testid="calc-div-frac"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${mulFrac ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Fraction ÷</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input value={a} onChange={(e) => setA(numVal(e.target.value))} placeholder="0" data-testid="calc-a"
                        className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      <span className="text-2xl font-bold">÷</span>
                      <input value={b} onChange={(e) => setB(numVal(e.target.value))} placeholder="0" data-testid="calc-b"
                        className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input value={a} onChange={(e) => setA(numVal(e.target.value))} placeholder="0" data-testid="calc-a"
                      className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                    <span className="text-2xl font-bold">{OPS[op]}</span>
                    <input value={b} onChange={(e) => setB(numVal(e.target.value))} placeholder="0" data-testid="calc-b"
                      className="w-24 h-11 text-center text-lg font-bold rounded-lg border-2 border-slate-900 outline-none" />
                  </div>
                )}
                <button onClick={layout} data-testid="calc-setout"
                  className="rounded-full bg-slate-900 text-white text-sm font-parent font-semibold px-4 py-2">Set it out</button>
                {fb && !fb.ok && <p className="font-parent text-sm text-red-600">{fb.msg}</p>}
              </div>
            ) : op === "ratio" ? (
              <div data-testid="calc-ratio-wrap" className="font-parent">
                <p className="text-xs text-slate-500 mb-3">Set it up as a proportion and put the unknown last, then cross-multiply: <span className="font-bold">? = (b × c) ÷ a</span>.</p>
                {/* optional column labels */}
                <div className="flex items-center gap-2 mb-2">
                  <input value={ratioLabels[0]} onChange={(e) => setRatioLabels((p) => [e.target.value.slice(0, 16), p[1]])} placeholder="label (e.g. distance)" data-testid="calc-ratio-lab-0"
                    className="w-32 h-8 px-2 text-xs font-semibold rounded-md border-2 border-slate-300 outline-none focus:border-blue-400" />
                  <span className="text-slate-400 text-xs">↔</span>
                  <input value={ratioLabels[1]} onChange={(e) => setRatioLabels((p) => [p[0], e.target.value.slice(0, 16)])} placeholder="label (e.g. time)" data-testid="calc-ratio-lab-1"
                    className="w-32 h-8 px-2 text-xs font-semibold rounded-md border-2 border-slate-300 outline-none focus:border-blue-400" />
                </div>
                {/* a : b = c : ? */}
                <div className="flex items-center gap-2 text-xl font-bold mb-4 flex-wrap">
                  <input value={ratio.a} onChange={(e) => setRatio((r) => ({ ...r, a: numVal(e.target.value) }))} placeholder="a" data-testid="calc-ratio-a"
                    className="w-16 h-10 text-center rounded-md border-2 border-slate-900 outline-none" />
                  <span className="text-slate-500">:</span>
                  <input value={ratio.b} onChange={(e) => setRatio((r) => ({ ...r, b: numVal(e.target.value) }))} placeholder="b" data-testid="calc-ratio-b"
                    className="w-16 h-10 text-center rounded-md border-2 border-slate-900 outline-none" />
                  <span className="text-slate-500 px-1">=</span>
                  <input value={ratio.c} onChange={(e) => setRatio((r) => ({ ...r, c: numVal(e.target.value) }))} placeholder="c" data-testid="calc-ratio-c"
                    className="w-16 h-10 text-center rounded-md border-2 border-slate-900 outline-none" />
                  <span className="text-slate-500">:</span>
                  <input value={ratio.x} onChange={(e) => setRatio((r) => ({ ...r, x: numVal(e.target.value) }))} placeholder="?" data-testid="calc-ratio-x"
                    className="w-20 h-10 text-center rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                </div>
                {/* working: ? = (b × c) ÷ a */}
                <div className="flex items-center gap-2 text-base font-bold mb-2 flex-wrap">
                  <span className="text-slate-500">? = (</span>
                  <span className="text-slate-700">{ratio.b || "b"}</span><span className="text-slate-400">×</span><span className="text-slate-700">{ratio.c || "c"}</span>
                  <span className="text-slate-500">) ÷</span>
                  <span className="text-slate-700">{ratio.a || "a"}</span>
                  <span className="text-slate-500">=</span>
                  <input value={ratio.prod} onChange={(e) => setRatio((r) => ({ ...r, prod: numVal(e.target.value) }))} placeholder="b×c" data-testid="calc-ratio-prod"
                    className="w-20 h-9 text-center rounded-md border-2 border-slate-900 outline-none" />
                  <span className="text-slate-500">÷</span>
                  <span className="text-slate-700">{ratio.a || "a"}</span>
                </div>
                <p className="text-[11px] text-slate-400">Tip: convert units first (e.g. km→m, h→s) using Notes, then fill a : b = c : ?</p>
                {fb && <p className={`mt-2 text-sm font-semibold ${fb.ok ? "text-green-600" : "text-slate-500"}`} data-testid="calc-feedback">{fb.msg}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={check} data-testid="calc-check" className="rounded-full bg-green-600 text-white text-sm px-3 py-1.5 flex items-center gap-1"><Check size={14} /> Check</button>
                  <button onClick={() => { setRatio({ a: "", b: "", c: "", prod: "", x: "" }); setFb(null); }} data-testid="calc-ratio-clear" className="rounded-full border-2 border-slate-300 text-slate-600 text-sm px-3 py-1.5 flex items-center gap-1"><Eraser size={14} /> Clear</button>
                </div>
              </div>
            ) : op === "notes" ? (
              <div data-testid="calc-notes-wrap" className="font-parent">
                <p className="text-xs text-slate-500 mb-2">Write your working here — ratios, proportions, steps, anything. It saves automatically.</p>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="calc-notes"
                  placeholder={"e.g.\n55 km = 55000 m\n1 hr = 60 min\n55000 m → 60 min\n1 m → 60 / 55000\n1536 m → 60 / 55000 × 1536 = ..."}
                  rows={10}
                  className="w-full rounded-xl border-2 border-slate-900 outline-none focus:ring-2 focus:ring-blue-300 p-3 text-base leading-7 text-slate-800"
                  style={{ backgroundImage: "repeating-linear-gradient(#ffffff, #ffffff 27px, #e2e8f0 28px)", backgroundAttachment: "local" }} />
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => setNotes("")} data-testid="calc-notes-clear" className="rounded-full border-2 border-slate-300 text-slate-600 text-sm px-3 py-1.5 flex items-center gap-1"><Eraser size={14} /> Clear notes</button>
                </div>
              </div>
            ) : (
              <div>
                {decMul && (
                  <div className="font-parent mb-3" data-testid="calc-decmul-head">
                    <div className="flex items-center gap-2 text-base font-bold text-slate-500">
                      <span>{decMulA}</span><span>×</span><span>{decMulB}</span>
                      <span className="text-xs font-normal text-slate-400 ml-1">→ multiplie les chiffres, puis place la virgule</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <label className="text-sm font-semibold text-slate-500">Réponse finale =</label>
                      <input value={decAns} onChange={(e) => setDecAns(numVal(e.target.value))} placeholder="?" data-testid="calc-dec-ans"
                        className="w-28 h-9 text-center text-base font-bold rounded-lg border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                    </div>
                  </div>
                )}
                {isDecimal && (op === "add" || op === "sub") ? (
                  /* Decimal +/− : full posed column with the decimal point kept aligned */
                  (() => {
                    const { intW, fracW } = decDims();
                    const ops = decOperands();
                    const rowCells = (s) => { const { i, f } = decParts(s); return { int: i.padStart(intW, " ").split(""), frac: f.padEnd(fracW, " ").split("") }; };
                    const StatCell = ({ ch }) => <div className="w-9 h-9 flex items-center justify-center text-xl font-bold">{ch.trim()}</div>;
                    const Point = ({ tall }) => (fracW > 0 ? <div className={`w-3 flex ${tall ? "items-end pb-1" : "items-center"} justify-center text-2xl font-bold text-blue-600`}>.</div> : null);
                    return (
                      <div className="font-parent overflow-x-auto" data-testid="calc-dec-col">
                        <div className="inline-block">
                          {op === "add" ? (
                            <>
                              <div className="flex gap-1.5 justify-end mb-0.5 items-center">
                                <span className="w-4" />
                                {Array.from({ length: intW }).map((_, i) => <Cell key={i} small value={carry[i] || ""} onChange={(v) => setCell(carry, setCarry, i, v)} testid={`calc-dcarry-${i}`} />)}
                                {fracW > 0 && <div className="w-3" />}
                                {Array.from({ length: fracW }).map((_, i) => <Cell key={i} small value={carry[intW + i] || ""} onChange={(v) => setCell(carry, setCarry, intW + i, v)} testid={`calc-dcarry-${intW + i}`} />)}
                              </div>
                              <p className="text-right text-[9px] text-amber-600 mb-1 pr-1">carry</p>
                              {ops.map((s, ri) => { const c = rowCells(s); return (
                                <div key={ri} className="flex gap-1.5 justify-end items-center" data-testid={`calc-dec-row-${ri}`}>
                                  <span className="text-xl font-bold w-4 text-center">{ri === ops.length - 1 ? "+" : ""}</span>
                                  {c.int.map((ch, i) => <StatCell key={i} ch={ch} />)}
                                  <Point />
                                  {c.frac.map((ch, i) => <StatCell key={i} ch={ch} />)}
                                </div>
                              ); })}
                            </>
                          ) : (
                            <>
                              {(() => { const c = rowCells(ops[0]); return (
                                <div className="flex gap-1.5 justify-end items-end" data-testid="calc-dec-row-0">
                                  <span className="w-4" />
                                  {c.int.map((ch, i) => { const blank = !ch.trim(); const col = i; const on = !!crossed[`sub-${col}`]; return (
                                    <div key={i} className="relative w-9 h-14 flex items-end justify-center">
                                      {!blank && <input value={borrowTen[col] || ""} onChange={(e) => setCell(borrowTen, setBorrowTen, col, e.target.value)} inputMode="numeric" maxLength={1} data-testid={`calc-bten-${col}`} className="absolute top-0 left-1/2 -translate-x-[1.5rem] w-4 h-6 text-center text-[10px] font-bold rounded border border-blue-400 bg-blue-50 text-blue-600 outline-none z-20" />}
                                      {!blank && <input value={borrowRed[col] || ""} onChange={(e) => setCell(borrowRed, setBorrowRed, col, e.target.value)} inputMode="numeric" maxLength={1} data-testid={`calc-bred-${col}`} className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-6 text-center text-[10px] font-bold rounded border border-amber-400 bg-amber-50 text-amber-700 outline-none z-10" />}
                                      <span onClick={() => { if (!blank) setCrossed((p) => ({ ...p, [`sub-${col}`]: !p[`sub-${col}`] })); }} className={`text-xl font-bold pb-0.5 ${blank ? "" : "cursor-pointer"} ${on ? "line-through text-slate-400" : ""}`}>{ch.trim()}</span>
                                    </div>
                                  ); })}
                                  <Point tall />
                                  {c.frac.map((ch, i) => { const blank = !ch.trim(); const col = intW + i; const on = !!crossed[`sub-${col}`]; return (
                                    <div key={i} className="relative w-9 h-14 flex items-end justify-center">
                                      {!blank && <input value={borrowTen[col] || ""} onChange={(e) => setCell(borrowTen, setBorrowTen, col, e.target.value)} inputMode="numeric" maxLength={1} data-testid={`calc-bten-${col}`} className="absolute top-0 left-1/2 -translate-x-[1.5rem] w-4 h-6 text-center text-[10px] font-bold rounded border border-blue-400 bg-blue-50 text-blue-600 outline-none z-20" />}
                                      {!blank && <input value={borrowRed[col] || ""} onChange={(e) => setCell(borrowRed, setBorrowRed, col, e.target.value)} inputMode="numeric" maxLength={1} data-testid={`calc-bred-${col}`} className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-6 text-center text-[10px] font-bold rounded border border-amber-400 bg-amber-50 text-amber-700 outline-none z-10" />}
                                      <span onClick={() => { if (!blank) setCrossed((p) => ({ ...p, [`sub-${col}`]: !p[`sub-${col}`] })); }} className={`text-xl font-bold pb-0.5 ${blank ? "" : "cursor-pointer"} ${on ? "line-through text-slate-400" : ""}`}>{ch.trim()}</span>
                                    </div>
                                  ); })}
                                </div>
                              ); })()}
                              {(() => { const c = rowCells(ops[1]); return (
                                <div className="flex gap-1.5 justify-end items-center" data-testid="calc-dec-row-1">
                                  <span className="text-xl font-bold w-4 text-center">−</span>
                                  {c.int.map((ch, i) => <StatCell key={i} ch={ch} />)}
                                  <Point />
                                  {c.frac.map((ch, i) => <StatCell key={i} ch={ch} />)}
                                </div>
                              ); })()}
                              <p className="text-right text-[9px] text-amber-600 mt-1">tap a digit to cross it out · write what's left just above it · add a small 1 to its left</p>
                            </>
                          )}
                          <div className="border-t-4 border-slate-900 my-1.5" />
                          <div data-adv-row className="flex gap-1.5 justify-end items-center">
                            <span className="w-4" />
                            {Array.from({ length: intW }).map((_, i) => <Cell key={i} value={ans[i] || ""} onChange={(v) => setCell(ans, setAns, i, v)} testid={`calc-dans-${i}`} />)}
                            <Point />
                            {Array.from({ length: fracW }).map((_, i) => <Cell key={i} value={ans[intW + i] || ""} onChange={(v) => setCell(ans, setAns, intW + i, v)} testid={`calc-dans-${intW + i}`} />)}
                          </div>
                          <p className="font-parent text-[10px] text-slate-400 mt-2">Line up the decimal points. Keep the point in your answer.</p>
                        </div>
                      </div>
                    );
                  })()
                ) : isDecimal ? (
                  /* Decimal division: one working line, keep the decimal point */
                  <div className="font-parent" data-testid="calc-dec-work">
                    <div className="flex flex-wrap items-center gap-2 text-xl font-bold">
                      {decOperands().map((s, i) => (
                        <Fragment key={i}>
                          {i > 0 && <span className="text-slate-400">{OPS[op]}</span>}
                          <span data-testid={`calc-dec-op-${i}`}>{s}</span>
                        </Fragment>
                      ))}
                      <span className="text-slate-400">=</span>
                      <input value={decAns} onChange={(e) => setDecAns(numVal(e.target.value))} placeholder="?" data-testid="calc-dec-ans"
                        className="w-28 h-10 text-center text-lg font-bold rounded-lg border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                    </div>
                    <p className="font-parent text-xs text-slate-400 mt-2">Keep the decimal point in your working and in the answer.</p>
                  </div>
                ) : (op === "add" || op === "sub") && arithFrac ? (
                  /* Fraction +/− : common denominator (LCM), convert, then add/subtract numerators */
                  <div className="font-parent" data-testid="calc-fa-work">
                    <div className="flex gap-2 mb-3">
                      <button onClick={() => { setArithFrac(false); setLaid(false); setFb(null); }} data-testid="calc-fa-whole"
                        className="text-[11px] font-semibold rounded-full border-2 border-slate-300 text-slate-600 px-3 py-1">{op === "add" ? "Whole +" : "Whole −"}</button>
                      <button data-testid="calc-fa-frac"
                        className="text-[11px] font-semibold rounded-full border-2 bg-blue-500 text-white border-blue-500 px-3 py-1">{op === "add" ? "Fraction +" : "Fraction −"}</button>
                    </div>
                    {/* the two fractions with the operator */}
                    <div className="flex items-center gap-2 mb-3 text-lg font-bold">
                      {[0, 1].map((i) => (
                        <Fragment key={i}>
                          {i === 1 && <span className="text-slate-500">{op === "add" ? "+" : "−"}</span>}
                          <span className="inline-flex flex-col items-center leading-none">
                            <input value={faN[i].n} onChange={(e) => setFaN((p) => p.map((f, j) => (j === i ? { ...f, n: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="?" data-testid={`calc-fa-n-${i}`}
                              className="w-11 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none" />
                            <span className="border-t-2 border-slate-900 w-full my-0.5" />
                            <input value={faN[i].d} onChange={(e) => setFaN((p) => p.map((f, j) => (j === i ? { ...f, d: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="?" data-testid={`calc-fa-d-${i}`}
                              className="w-11 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none" />
                          </span>
                        </Fragment>
                      ))}
                    </div>
                    {/* common denominator */}
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-sm font-semibold text-slate-500">Common denominator (LCM) =</label>
                      <input value={faLcm} onChange={(e) => setFaLcm(onlyDigits(e.target.value).slice(0, 5))} data-testid="calc-fa-lcm"
                        className="w-16 h-9 text-center text-base font-bold rounded-lg border-2 border-slate-900 outline-none" />
                    </div>
                    {/* conversion rows */}
                    <div className="space-y-3">
                      {[0, 1].map((i) => (
                        <div key={i} className="flex items-center gap-2 text-lg font-bold" data-testid={`calc-fa-row-${i}`}>
                          <span className="inline-flex flex-col items-center leading-none">
                            <span className="px-1">{faN[i].n || "?"}</span>
                            <span className="border-t-2 border-slate-900 w-full text-center px-1">{faN[i].d || "?"}</span>
                          </span>
                          <span className="text-slate-400 text-base">×</span>
                          <span className="inline-flex flex-col items-center leading-none">
                            <input value={faMul[i]} onChange={(e) => setFaMul((p) => p.map((x, j) => (j === i ? onlyDigits(e.target.value).slice(0, 3) : x)))} placeholder="?" data-testid={`calc-fa-mul-${i}`}
                              className="w-10 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                            <span className="border-t-2 border-slate-900 w-full my-0.5" />
                            <span className="text-sm text-slate-500 text-center">×{faMul[i] || "?"}</span>
                          </span>
                          <span className="text-slate-400 text-base">=</span>
                          <span className="inline-flex flex-col items-center leading-none">
                            <input value={faNum[i]} onChange={(e) => setFaNum((p) => p.map((x, j) => (j === i ? onlyDigits(e.target.value).slice(0, 5) : x)))} placeholder="?" data-testid={`calc-fa-num-${i}`}
                              className="w-12 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none" />
                            <span className="border-t-2 border-slate-900 w-full my-0.5" />
                            <span className="w-12 h-8 flex items-center justify-center text-base font-bold text-slate-700">{faLcm || "?"}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* final result */}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t-2 border-slate-200 text-lg font-bold">
                      <span className="inline-flex flex-col items-center leading-none text-slate-500 text-base">
                        <span className="px-1">{faNum[0] || "?"} {op === "add" ? "+" : "−"} {faNum[1] || "?"}</span>
                        <span className="border-t-2 border-slate-400 w-full text-center px-1">{faLcm || "?"}</span>
                      </span>
                      <span className="text-slate-400">=</span>
                      <span className="inline-flex flex-col items-center leading-none">
                        <input value={faR.n} onChange={(e) => setFaR((r) => ({ ...r, n: onlyDigits(e.target.value).slice(0, 6) }))} placeholder="?" data-testid="calc-fa-rn"
                          className="w-14 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                        <span className="border-t-2 border-slate-900 w-full my-0.5" />
                        <input value={faR.d} onChange={(e) => setFaR((r) => ({ ...r, d: onlyDigits(e.target.value).slice(0, 6) }))} placeholder="?" data-testid="calc-fa-rd"
                          className="w-14 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                      </span>
                    </div>
                  </div>
                ) : (op === "mul" || op === "div") && mulFrac ? (
                  /* Fraction × / ÷ : cross-cancel then multiply the tops and bottoms */
                  <div className="font-parent" data-testid="calc-fm-work">
                    <div className="flex gap-2 mb-3">
                      <button onClick={() => { setMulFrac(false); setLaid(false); setFb(null); }} data-testid="calc-mul-whole-2"
                        className="text-[11px] font-semibold rounded-full border-2 border-slate-300 text-slate-600 px-3 py-1">{op === "div" ? "Whole ÷" : "Whole ×"}</button>
                      <button data-testid="calc-mul-frac-2"
                        className="text-[11px] font-semibold rounded-full border-2 bg-blue-500 text-white border-blue-500 px-3 py-1">{op === "div" ? "Fraction ÷" : "Fraction ×"}</button>
                    </div>
                    {op === "div" && (fmDivOrig.n || fmDivOrig.d) && (
                      <div className="flex items-center gap-2 mb-2 text-base font-bold text-slate-500" data-testid="calc-fm-div-head">
                        <span className="inline-flex flex-col items-center leading-none">
                          <span className="px-1">{fmN[0].n}</span>
                          <span className="border-t-2 border-slate-400 w-full text-center px-1">{fmN[0].d}</span>
                        </span>
                        <span>÷</span>
                        <span className="inline-flex flex-col items-center leading-none">
                          <span className="px-1">{fmDivOrig.n}</span>
                          <span className="border-t-2 border-slate-400 w-full text-center px-1">{fmDivOrig.d}</span>
                        </span>
                        <span className="text-slate-400 text-sm ml-1">→ flip &amp; multiply</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xl font-bold">
                      {fracCancel(0)}
                      <span className="text-slate-400">×</span>
                      {fracCancel(1)}
                      <span className="text-slate-400">=</span>
                      <span className="inline-flex flex-col items-center leading-none">
                        <input value={fmR.n} onChange={(e) => setFmR((r) => ({ ...r, n: onlyDigits(e.target.value).slice(0, 7) }))} placeholder="?" inputMode="numeric" data-testid="calc-fm-rn"
                          className="w-16 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                        <span className="border-t-2 border-slate-900 w-full my-0.5" />
                        <input value={fmR.d} onChange={(e) => setFmR((r) => ({ ...r, d: onlyDigits(e.target.value).slice(0, 7) }))} placeholder="?" inputMode="numeric" data-testid="calc-fm-rd"
                          className="w-16 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                      </span>
                    </div>
                    <p className="font-parent text-xs text-slate-400 mt-2">Cancel common factors (write the smaller number in red), then multiply tops and bottoms. A whole number n is n⁄1.</p>
                  </div>
                ) : op === "cmp" ? (
                  /* Compare fractions: convert both to a common denominator (LCM) */
                  <div className="font-parent" data-testid="calc-cmp-work">
                    {/* the question, as fractions */}
                    <div className="flex items-center gap-2 mb-3 text-lg font-bold">
                      <span className="text-slate-500 text-sm mr-1">Compare</span>
                      <span className="inline-flex flex-col items-center leading-none">
                        <span className="px-1">{cmpN[0].n}</span>
                        <span className="border-t-2 border-slate-900 w-full text-center px-1">{cmpN[0].d}</span>
                      </span>
                      <span className="text-slate-400">?</span>
                      <span className="inline-flex flex-col items-center leading-none">
                        <span className="px-1">{cmpN[1].n}</span>
                        <span className="border-t-2 border-slate-900 w-full text-center px-1">{cmpN[1].d}</span>
                      </span>
                    </div>
                    {/* common denominator (LCM) — computed by the child in LCM/HCF mode */}
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-sm font-semibold text-slate-500">Common denominator (LCM) =</label>
                      <input value={cmpLcm} onChange={(e) => setCmpLcm(onlyDigits(e.target.value).slice(0, 5))} data-testid="calc-cmp-lcm"
                        className="w-16 h-9 text-center text-base font-bold rounded-lg border-2 border-slate-900 outline-none" />
                    </div>
                    {/* conversion rows: fraction × m/m = new/LCM */}
                    <div className="space-y-3">
                      {[0, 1].map((i) => (
                        <div key={i} className="flex items-center gap-2 text-lg font-bold" data-testid={`calc-cmp-row-${i}`}>
                          <span className="inline-flex flex-col items-center leading-none">
                            <input value={cmpN[i].n} onChange={(e) => setCmpN((p) => p.map((f, j) => (j === i ? { ...f, n: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="?" data-testid={`calc-cmp-rn-${i}`}
                              className="w-11 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none" />
                            <span className="border-t-2 border-slate-900 w-full my-0.5" />
                            <input value={cmpN[i].d} onChange={(e) => setCmpN((p) => p.map((f, j) => (j === i ? { ...f, d: onlyDigits(e.target.value).slice(0, 7) } : f)))} placeholder="?" data-testid={`calc-cmp-rd-${i}`}
                              className="w-11 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none" />
                          </span>
                          <span className="text-slate-400 text-base">×</span>
                          <span className="inline-flex flex-col items-center leading-none">
                            <div className="flex items-center gap-0.5"><span className="text-slate-400 text-xs">×</span>
                              <input value={cmpMul[i]} onChange={(e) => setCmpMul((p) => p.map((x, j) => (j === i ? onlyDigits(e.target.value).slice(0, 3) : x)))} placeholder="?" data-testid={`calc-cmp-mul-${i}`}
                                className="w-9 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" /></div>
                            <span className="border-t-2 border-slate-900 w-full my-0.5" />
                            <div className="flex items-center gap-0.5"><span className="text-slate-400 text-xs">×</span>
                              <input value={cmpMul[i]} onChange={(e) => setCmpMul((p) => p.map((x, j) => (j === i ? onlyDigits(e.target.value).slice(0, 3) : x)))} placeholder="?" data-testid={`calc-cmp-mulb-${i}`}
                                className="w-9 h-8 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" /></div>
                          </span>
                          <span className="text-slate-400 text-base">=</span>
                          <span className="inline-flex flex-col items-center leading-none">
                            <input value={cmpNum[i]} onChange={(e) => setCmpNum((p) => p.map((x, j) => (j === i ? onlyDigits(e.target.value).slice(0, 5) : x)))} placeholder="?" data-testid={`calc-cmp-num-${i}`}
                              className="w-12 h-8 text-center text-base font-bold rounded-md border-2 border-slate-900 outline-none" />
                            <span className="border-t-2 border-slate-900 w-full my-0.5" />
                            <span className="w-12 h-8 flex items-center justify-center text-base font-bold text-slate-700">{cmpLcm || "?"}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* final comparison of the converted fractions */}
                    <div className="flex items-center gap-3 mt-4 pt-3 border-t-2 border-slate-200 text-lg font-bold">
                      <span className="inline-flex flex-col items-center leading-none">
                        <span className="px-1">{cmpNum[0] || "?"}</span>
                        <span className="border-t-2 border-slate-900 w-full text-center px-1">{cmpLcm || "?"}</span>
                      </span>
                      <div className="flex gap-1">
                        {[">", "<", "="].map((sym) => (
                          <button key={sym} onClick={() => setCmpOp(sym)} data-testid={`calc-cmp-op-${sym === ">" ? "gt" : sym === "<" ? "lt" : "eq"}`}
                            className={`w-9 h-9 rounded-lg border-2 border-slate-900 text-lg font-bold ${cmpOp === sym ? "bg-blue-500 text-white" : "bg-white"}`}>{sym}</button>
                        ))}
                      </div>
                      <span className="inline-flex flex-col items-center leading-none">
                        <span className="px-1">{cmpNum[1] || "?"}</span>
                        <span className="border-t-2 border-slate-900 w-full text-center px-1">{cmpLcm || "?"}</span>
                      </span>
                    </div>
                  </div>
                ) : op === "fact" ? (
                  /* LCM / HCF prime-factor ladder */
                  <div className="overflow-x-auto"><div className="inline-block">
                    {/* choose the method */}
                    <div className="flex gap-2 mb-2 font-parent">
                      <button onClick={() => setFactMode("separate")} data-testid="calc-fact-separate"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${factMode === "separate" ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Each on its own</button>
                      <button onClick={() => setFactMode("together")} data-testid="calc-fact-together"
                        className={`text-[11px] font-semibold rounded-full border-2 px-3 py-1 ${factMode === "together" ? "bg-blue-500 text-white border-blue-500" : "border-slate-300 text-slate-600"}`}>Share a prime</button>
                    </div>

                    {factMode === "together" ? (
                      <>
                        <div className="flex gap-1.5 items-center">
                          <div className="w-10 shrink-0" />
                          {factTop.map((n, i) => (
                            <div key={i} className="w-14 h-10 flex items-center justify-center text-xl font-bold" data-testid={`calc-facttop-${i}`}>{n}</div>
                          ))}
                        </div>
                        {factRows.map((row, r) => (
                          <div key={r} className="flex gap-1.5 items-center border-t border-slate-200">
                            <input value={row.p[0] || ""} onChange={(e) => setFactPrime(r, 0, e.target.value)} inputMode="numeric" placeholder="÷" data-testid={`calc-factp-${r}`}
                              className="w-10 h-10 shrink-0 text-center text-lg font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none" />
                            <div className="border-l-[3px] border-slate-900 pl-1.5 flex gap-1.5">
                              {row.q.map((qv, i) => (
                                <input key={i} value={qv} onChange={(e) => setFactQuot(r, i, e.target.value)} inputMode="numeric" data-testid={`calc-factq-${r}-${i}`}
                                  className="w-14 h-10 text-center text-lg font-bold rounded-md border-2 border-slate-900 bg-white outline-none focus:ring-2 focus:ring-blue-300" />
                              ))}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      /* separate: classic staircase — each row is [divisor | number],
                         the number divided down to 1 in its own ladder (like on paper) */
                      <div className="flex gap-4">
                        {factTop.map((n, i) => {
                          const qvals = factRows.map((row) => (row.q[i] || "").trim());
                          const hitOne = qvals.findIndex((v) => v === "1");
                          const lastRow = hitOne === -1 ? factRows.length - 1 : hitOne;
                          return (
                            <div key={i} className="inline-block" data-testid={`calc-factcol-${i}`}>
                              {factRows.slice(0, lastRow + 1).map((row, r) => (
                                <div key={r} className="flex items-stretch">
                                  <input value={row.p[i] || ""} onChange={(e) => setFactPrime(r, i, e.target.value)} inputMode="numeric" placeholder="÷" data-testid={`calc-factp-${r}-${i}`}
                                    className="w-8 h-10 shrink-0 text-center text-base font-bold rounded-md border-2 border-blue-400 bg-blue-50 text-blue-700 outline-none mb-1" />
                                  <div className="border-l-[3px] border-b-2 border-slate-900 pl-1.5 pr-1 pb-0.5">
                                    {r === 0 ? (
                                      <div className="w-11 h-9 flex items-center justify-center text-xl font-bold" data-testid={`calc-facttop-${i}`}>{n}</div>
                                    ) : (
                                      <input value={factRows[r - 1].q[i] || ""} onChange={(e) => setFactQuot(r - 1, i, e.target.value)} inputMode="numeric" data-testid={`calc-factq-${r - 1}-${i}`}
                                        className="w-11 h-9 text-center text-lg font-bold rounded-md border-2 border-slate-900 bg-white outline-none focus:ring-2 focus:ring-blue-300" />
                                    )}
                                  </div>
                                </div>
                              ))}
                              {/* the final quotient (reaches 1), written under the ladder */}
                              {factRows.length > 0 && (
                                <div className="flex items-stretch">
                                  <div className="w-8 shrink-0" />
                                  <div className="pl-1.5 pr-1">
                                    <input value={factRows[lastRow].q[i] || ""} onChange={(e) => setFactQuot(lastRow, i, e.target.value)} inputMode="numeric" data-testid={`calc-factq-${lastRow}-${i}`}
                                      className="w-11 h-9 mt-1 text-center text-lg font-bold rounded-md border-2 border-slate-900 bg-white outline-none focus:ring-2 focus:ring-blue-300" />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <button onClick={addFactRow} data-testid="calc-fact-addrow"
                      className="mt-2 rounded-full border-2 border-dashed border-slate-300 text-slate-500 text-[11px] font-parent font-semibold px-3 py-1">+ Divide again (next prime)</button>
                    <p className="text-[9px] font-parent text-amber-600 mt-1">Keep dividing by a prime (2, 3, 5, 7…) until every number becomes 1. If a prime doesn't divide a number, copy it down unchanged.</p>

                    {/* bottom: auto-aligned prime factors, then LCM / HCF */}
                    <div className="border-t-4 border-slate-900 my-2" />
                    <button onClick={autoFillFactors} data-testid="calc-fact-auto"
                      className="mb-2 inline-flex items-center gap-1 rounded-full border-2 border-slate-900 bg-yellow-300 text-slate-900 text-[11px] font-parent font-bold px-3 py-1.5">
                      <Sparkles size={12} /> Fill factors automatically
                    </button>
                    {factGrid && (
                      <div className="space-y-1 font-parent mb-3" data-testid="calc-factgrid">
                        {factTop.map((n, ri) => (
                          <div key={ri} className="flex items-center gap-1" data-testid={`calc-factline-${ri}`}>
                            <span className="text-base font-bold w-9 text-right shrink-0">{n} =</span>
                            {factGrid.columns.map((p, ci) => (
                              <Fragment key={ci}>
                                {ci > 0 && <span className="w-3 text-center text-slate-400 text-sm">×</span>}
                                <span className="w-7 h-7 flex items-center justify-center text-base font-bold text-blue-700">{factGrid.rows[ri][ci] ?? ""}</span>
                              </Fragment>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-1.5 font-parent w-72 max-w-full">
                      <div className="flex items-center gap-2 pt-1">
                        <label className="text-sm font-semibold text-slate-500 w-12 shrink-0">LCM =</label>
                        <input value={lcmExpr} onChange={(e) => setLcmExpr(e.target.value)} placeholder="2 × 2 × 3" data-testid="calc-lcmexpr"
                          className="flex-1 min-w-0 h-8 px-2 text-sm font-semibold rounded-lg border-2 border-slate-300 outline-none focus:border-blue-400" />
                        <span className="font-bold shrink-0">=</span>
                        <input value={factLCM} onChange={(e) => setFactLCM(onlyDigits(e.target.value))} data-testid="calc-lcm"
                          className="w-14 h-8 shrink-0 text-center text-base font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-slate-500 w-12 shrink-0">HCF =</label>
                        <input value={hcfExpr} onChange={(e) => setHcfExpr(e.target.value)} placeholder="common" data-testid="calc-hcfexpr"
                          className="flex-1 min-w-0 h-8 px-2 text-sm font-semibold rounded-lg border-2 border-slate-300 outline-none focus:border-blue-400" />
                        <span className="font-bold shrink-0">=</span>
                        <input value={factHCF} onChange={(e) => setFactHCF(onlyDigits(e.target.value))} data-testid="calc-hcf"
                          className="w-14 h-8 shrink-0 text-center text-base font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      </div>
                    </div>
                  </div></div>
                ) : op === "div" ? (
                  b.length < 2 ? (
                    /* SHORT DIVISION (1-figure divisor): quotient written below,
                       carried remainders as small superscripts before each next digit */
                    <div className="overflow-x-auto"><div className="inline-block">
                      <div className="flex items-start gap-1">
                        <div className="text-2xl font-bold pr-1" style={{ marginTop: "2px" }} data-testid="calc-divisor">{b}</div>
                        <div className="inline-block">
                          {/* dividend with a left bar (short-division bracket) */}
                          <div className="border-l-[3px] border-slate-900 pl-2">
                            <div className="flex gap-1.5 pl-1 pb-1">
                              {String(a).split("").map((d, i) => (
                                <div key={i} className="relative w-9 h-10 flex items-center justify-center">
                                  {i > 0 && (
                                    <input value={divCarry[i] || ""} onChange={(e) => setCell(divCarry, setDivCarry, i, e.target.value)} inputMode="numeric" maxLength={1}
                                      data-testid={`calc-dcarry-${i}`}
                                      className="absolute top-0 -left-2 w-4 h-5 text-center text-[10px] font-bold rounded border border-blue-400 bg-blue-50 text-blue-600 outline-none z-10" placeholder="" />
                                  )}
                                  <span onClick={() => setCrossed((p) => ({ ...p, [`divd-${i}`]: !p[`divd-${i}`] }))}
                                    className={`text-xl font-bold cursor-pointer select-none ${crossed[`divd-${i}`] ? "line-through text-slate-400" : ""}`} data-testid={`calc-dividend-${i}`}>{d}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* horizontal line under the dividend */}
                          <div className="border-t-[3px] border-slate-900" />
                          {/* quotient below the line (aligned with the dividend) */}
                          <div className="flex gap-1.5 mt-1" style={{ paddingLeft: "15px" }}>
                            {divQuot.map((c, i) => <Cell key={i} value={c} onChange={(v) => setCell(divQuot, setDivQuot, i, v)} testid={`calc-q-${i}`} />)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 font-parent text-sm">
                        <label className="text-slate-500">Remainder</label>
                        <input value={remainder} onChange={(e) => setRemainder(onlyDigits(e.target.value))} data-testid="calc-remainder"
                          className="w-16 h-9 text-center font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      </div>
                      <p className="text-[9px] font-parent text-amber-600 mt-1">short division: write each quotient digit below the line · carry the leftover as a small number before the next digit · tap a digit to cross it out</p>
                    </div></div>
                  ) : (
                    /* LONG DIVISION (2-figure divisor): quotient on top, bring-down and
                       multiply-subtract working below the bracket */
                    <div className="overflow-x-auto"><div className="inline-block min-w-full">
                      <div className="flex items-start gap-2">
                        {/* divisor on the left (digits crossable), aligned with the dividend row */}
                        <div className="flex items-center gap-0.5" style={{ height: "2.5rem", marginTop: "2.75rem" }} data-testid="calc-divisor">
                          {String(b).split("").map((d, i) => {
                            const on = !!crossed[`divs-${i}`];
                            return (
                              <span key={i} onClick={() => setCrossed((p) => ({ ...p, [`divs-${i}`]: !p[`divs-${i}`] }))}
                                className={`text-2xl font-bold cursor-pointer select-none ${on ? "line-through text-slate-400" : ""}`}>{d}</span>
                            );
                          })}
                        </div>
                        <div className="inline-block">
                          {/* quotient boxes on top */}
                          <div className="flex gap-1.5 mb-1" style={{ paddingLeft: "7px" }}>
                            <div className="w-6 shrink-0" />
                            {divQuot.map((c, i) => <Cell key={i} value={c} onChange={(v) => setCell(divQuot, setDivQuot, i, v)} testid={`calc-q-${i}`} />)}
                          </div>
                          {/* division "house": top + left border only over the dividend row */}
                          <div className="border-l-[3px] border-t-[3px] border-slate-900 rounded-tl-md pt-1" style={{ paddingLeft: "4px" }}>
                            <div className="flex gap-1.5">
                              <div className="w-6 shrink-0" />
                              {String(a).split("").map((d, i) => {
                                const on = !!crossed[`divd-${i}`];
                                return (
                                  <div key={i} className="relative pt-4">
                                    <BorrowBoxes k={`dv-${i}`} annot={annot} onChange={setAnnotVal} />
                                    <div onClick={() => setCrossed((p) => ({ ...p, [`divd-${i}`]: !p[`divd-${i}`] }))}
                                      data-testid={`calc-dividend-${i}`}
                                      className={`w-9 h-10 flex items-center justify-center text-xl font-bold cursor-pointer select-none ${on ? "line-through text-slate-400" : ""}`}>{d}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* working rows below the house — each already has a − sign and a line */}
                          <div style={{ paddingLeft: "7px" }}>
                            {divWork.map((row, r) => (
                              <div key={r} data-adv-row className={`flex gap-1.5 items-end mt-1 ${divSigns[r] ? "border-b-2 border-slate-900 pb-1" : ""}`}>
                                <input value={divSigns[r] || ""} onChange={(e) => setDivSign(r, e.target.value)} maxLength={1} data-testid={`calc-dsign-${r}`}
                                  className="w-6 h-9 shrink-0 text-center text-xl font-bold rounded-md border-2 border-slate-300 bg-slate-50 text-slate-700 outline-none focus:ring-2 focus:ring-blue-300" placeholder="" />
                                {row.map((c, i) => (
                                  <div key={i} className={`relative ${divSigns[r] ? "" : "pt-4"}`}>
                                    {!divSigns[r] && <BorrowBoxes k={`dw-${r}-${i}`} annot={annot} onChange={setAnnotVal} />}
                                    <Cell value={c} onChange={(v) => setWork(r, i, v)} testid={`calc-dw-${r}-${i}`} />
                                  </div>
                                ))}
                              </div>
                            ))}
                            <button onClick={addWorkRow} data-testid="calc-add-workrow"
                              className="mt-2 rounded-full border-2 border-dashed border-slate-300 text-slate-500 text-[11px] font-parent font-semibold px-3 py-1">+ Add row</button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 font-parent text-sm">
                        <label className="text-slate-500">Remainder</label>
                        <input value={remainder} onChange={(e) => setRemainder(onlyDigits(e.target.value))} data-testid="calc-remainder"
                          className="w-16 h-9 text-center font-bold rounded-lg border-2 border-slate-900 outline-none" />
                      </div>
                      <p className="text-[9px] font-parent text-amber-600 mt-1">long division: quotient on top · tap a dividend/divisor digit to cross it out (bring it down) · type − to subtract (a line appears) · + Add row for each step</p>
                    </div></div>
                  )
                ) : longMul ? (
                  <div className="overflow-x-auto"><div className="inline-block">
                    <div className="flex justify-end mb-1">
                      <button onClick={swap} data-testid="calc-swap"
                        className="inline-flex items-center gap-1 rounded-full border-2 border-slate-900 bg-white text-slate-700 text-[11px] font-parent font-bold px-2.5 py-1">
                        <ArrowUpDown size={12} /> Swap
                      </button>
                    </div>
                    {/* carry boxes for the top multiplication */}
                    <div className="flex gap-1.5 justify-end mb-0.5">
                      {carry.map((c, i) => <Cell key={i} value={c} onChange={(v) => setCell(carry, setCarry, i, v)} testid={`calc-carry-${i}`} small />)}
                    </div>
                    <p className="text-right text-[9px] font-parent text-amber-600 mb-1 pr-1">carry</p>
                    {/* top number */}
                    <div className="flex gap-1.5 justify-end">
                      {pad(a, cols).map((d, i) => <DigitCell key={i} id={`top-${i}`} ch={d} />)}
                    </div>
                    {/* multiplier */}
                    <div className="flex gap-1.5 justify-end items-center">
                      <span className="text-xl font-bold mr-1">×</span>
                      {pad(b, cols).map((d, i) => <DigitCell key={i} id={`mul-${i}`} ch={d} />)}
                    </div>
                    <div className="border-t-4 border-slate-900 my-1.5" />
                    {/* addition part: 2 rows of boxes (one partial product per multiplier digit) */}
                    {pRows.map((row, j) => (
                      <div key={j} className="flex gap-1.5 justify-end mb-1 items-center">
                        {row.map((c, i) => (
                          <Cell key={i} value={row[i]} onChange={(v) => setNested(j, i, v)} testid={`calc-p${j}-${i}`} />
                        ))}
                      </div>
                    ))}
                    <div className="border-t-4 border-slate-900 my-1.5" />
                    {/* final answer: a single row of boxes */}
                    <div className="flex gap-1.5 justify-end">
                      {ans.map((c, i) => <Cell key={i} value={c} onChange={(v) => setCell(ans, setAns, i, v)} testid={`calc-ans-${i}`} />)}
                    </div>
                  </div></div>
                ) : (
                  <div className="overflow-x-auto"><div className="inline-block">
                    {op === "sub" ? (
                      <>
                        {/* top number: tap a digit to cross out. Small blue box on its LEFT = the
                            borrowed 1 (makes 10 / 13...). Small amber box on its RIGHT = what's left. */}
                        <div className="flex gap-1.5 justify-end pl-2">
                          {pad(a, cols).map((d, i) => {
                            const blank = !d.trim();
                            const on = !!crossed[`sub-${i}`];
                            return (
                              <div key={i} className="relative w-9 h-14 flex items-end justify-center">
                                {!blank && (
                                  <input value={borrowTen[i] || ""} onChange={(e) => setCell(borrowTen, setBorrowTen, i, e.target.value)} inputMode="numeric" maxLength={1}
                                    data-testid={`calc-bten-${i}`}
                                    className="absolute top-0 left-1/2 -translate-x-[1.5rem] w-4 h-6 text-center text-[10px] font-bold rounded border border-blue-400 bg-blue-50 text-blue-600 outline-none z-20" placeholder="" />
                                )}
                                {!blank && (
                                  <input value={borrowRed[i] || ""} onChange={(e) => setCell(borrowRed, setBorrowRed, i, e.target.value)} inputMode="numeric" maxLength={1}
                                    data-testid={`calc-bred-${i}`}
                                    className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-6 text-center text-[10px] font-bold rounded border border-amber-400 bg-amber-50 text-amber-700 outline-none z-10" placeholder="" />
                                )}
                                <span onClick={() => { if (!blank) setCrossed((p) => ({ ...p, [`sub-${i}`]: !p[`sub-${i}`] })); }}
                                  className={`text-xl font-bold pb-0.5 ${blank ? "" : "cursor-pointer"} ${on ? "line-through text-slate-400" : ""}`}>{d.trim()}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex gap-1.5 justify-end items-center pl-2">
                          <span className="text-xl font-bold mr-1">−</span>
                          {pad(b, cols).map((d, i) => <div key={i} className="w-9 h-9 flex items-center justify-center text-xl font-bold">{d.trim()}</div>)}
                        </div>
                        <p className="text-right text-[9px] font-parent text-amber-600 mt-1">tap a digit to cross it out · write what's left just above it (8→7) · add a 1 to its left to make e.g. 17</p>
                      </>
                    ) : (
                      <>
                        <div className="flex gap-1.5 justify-end mb-0.5">
                          {carry.map((c, i) => <Cell key={i} value={c} onChange={(v) => setCell(carry, setCarry, i, v)} testid={`calc-carry-${i}`} small />)}
                        </div>
                        <p className="text-right text-[9px] font-parent text-amber-600 mb-1 pr-1">carry</p>
                        {useAddends ? (
                          addends.map((t, ri) => (
                            <div key={ri} className="flex gap-1.5 justify-end items-center">
                              <span className="text-xl font-bold mr-1">{ri === addends.length - 1 ? "+" : ""}</span>
                              {pad(t, cols).map((d, i) => <div key={i} className="w-9 h-9 flex items-center justify-center text-xl font-bold">{d.trim()}</div>)}
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="flex gap-1.5 justify-end">
                              {pad(a, cols).map((d, i) => <div key={i} className="w-9 h-9 flex items-center justify-center text-xl font-bold">{d.trim()}</div>)}
                            </div>
                            <div className="flex gap-1.5 justify-end items-center">
                              <span className="text-xl font-bold mr-1">{OPS[op]}</span>
                              {pad(b, cols).map((d, i) => <div key={i} className="w-9 h-9 flex items-center justify-center text-xl font-bold">{d.trim()}</div>)}
                            </div>
                          </>
                        )}
                      </>
                    )}
                    <div className="border-t-4 border-slate-900 my-1.5" />
                    <div className="flex gap-1.5 justify-end">
                      {ans.map((c, i) => <Cell key={i} value={c} onChange={(v) => setCell(ans, setAns, i, v)} testid={`calc-ans-${i}`} />)}
                    </div>
                  </div></div>
                )}
                {fb && <p className={`mt-2 font-parent text-sm font-semibold ${fb.ok ? "text-green-600" : "text-slate-500"}`} data-testid="calc-feedback">{fb.msg}</p>}
                <div className="flex flex-wrap gap-2 mt-3 font-parent">
                  <button onClick={check} data-testid="calc-check" className="rounded-full bg-green-600 text-white text-sm px-3 py-1.5 flex items-center gap-1"><Check size={14} /> Check</button>
                  <button onClick={clearWork} data-testid="calc-clear" className="rounded-full border-2 border-slate-300 text-slate-600 text-sm px-3 py-1.5 flex items-center gap-1"><Eraser size={14} /> Clear</button>
                  <button onClick={reset} data-testid="calc-new" className="rounded-full text-slate-500 text-sm px-3 py-1.5 flex items-center gap-1"><RotateCcw size={14} /> New</button>
                </div>
              </div>
            )}
    </motion.div>
  );
}
